import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { Track } from '@/world/Track';
import { tuning } from '@/config/tuning';
import type { PlacedShortcut, KickerFrame } from '@/physics/shortcuts';
import {
  art,
  box,
  canvasTex,
  chStrip,
  dressExit,
  glowStrips,
  mat,
  nodeAt,
} from '@/world/scenery/shortcutMeshes';

/**
 * shortcutNapoli.ts — the ROOFTOPS OF SPACCANAPOLI (Napoli's batch-2 secret
 * shortcut, `look: 'rooftop'`). This file owns everything the ride wears —
 * launcher, deck, roof life, exit — and edits nothing outside itself (the
 * Tokyo build lives in shortcutTokyo.ts; the dispatch in shortcutMeshes.ts).
 *
 * THE DESIGN — "over the roofs of the old town":
 *  - LAUNCHER: a RENOVATION SITE against the course wall (the M117d
 *    doctrine: looks like city works, operates as a ramp). M117f owner
 *    round — "the ramps still look too much like ramps and not enough
 *    like roadwork": the clean symmetric plank wedge with a glowing
 *    centre strip is GONE. It is now three UNEVEN plank runs (one short
 *    at the foot, one board jutting past the lip) on an ASYMMETRIC tube
 *    frame (a brace where a post is missing), a sagging tarpaulin over
 *    the upper frame, and a working YARD around it: cement mixer, spoil
 *    heap, sandbags, kerbstone and tube stacks, a wheelbarrow, hoarding
 *    panels, mortar spill and timber offcuts. The go-fast cue is now
 *    INCIDENTAL — worn hazard tape at the lip plus a faint streaky wet
 *    sheen on the boards, never a lit runway.
 *  - THE ROOFTOP PITCH (M117f owner ask): a caged five-a-side terrace
 *    alongside the deck with a game in progress — see the PITCH block
 *    for the measured placement (it must be IN FRAME while riding).
 *  - THE DECK IS NOT A BRIDGE: it reads as a CONTINUOUS BLOCK OF HOUSES
 *    whose flat tiled terraces you ride. Floor = terracotta barrel-tile
 *    courses; edges = low cream stucco parapets (no steel rails); the
 *    underside skirt is PAINTED FACADES — pastel spaccanapoli house fronts
 *    with green shutters, iron balconies and a grimy base — dropping all
 *    the way to the ground, so the houses ARE the support (no columns).
 *  - ROOF LIFE outside the drivable width: chimney pots, water tanks, TV
 *    aerials, stair bulkheads, and LAUNDRY LINES spanning overhead between
 *    taller palazzo towers (cloth bottoms clear the chase camera).
 *  - EXIT: the "collapsed roof" — the tiled chute runs down to road grade
 *    between rubble berms, spilled tile shards and leaning timber props.
 *
 * MEASURED VISUAL GOTCHAS (encoded here, M117 style):
 *  - The chase camera rides ~4.8 u above the deck floor, and hung cloth
 *    reaches ~2.25 u below its line (hang gap + sag + cloth height): a
 *    laundry line at +6.5 put cloth bottoms at ~4.6 — INSIDE the camera.
 *    Lines hang at +8.5 so the lowest cloth clears 6.2 (the M116 rule),
 *    which in turn sizes the flanking towers (~8.8 over the deck — a real
 *    3-storey palazzo, which is what makes tall posts read believable).
 *  - The facade skirt CANNOT be one stretchy band: strip UV.y always spans
 *    0..1, so windows painted into a ground-reaching band stretch with the
 *    deck height (5.2 u at entry, 0.3 at the exit). Split it: a constant
 *    ~3.15 u WINDOW band hung from the deck edge + a featureless grimy
 *    base filler to the ground that absorbs all the stretch. Below ~2.3 u
 *    of clearance the window band collapses to a sliver (windows squashed
 *    to half height read as a rendering bug, not a low house).
 *  - Pastel variance must be painted INTO the facade canvas (4 house
 *    fronts per repeat): chStrip is one material per strip, so per-house
 *    tinting via materials would explode the draw-call budget.
 *  - No flat emissive on the launcher planks (the M117d wash lesson: an
 *    unmapped emissive tint turns the whole slab into a glowing wedge) —
 *    and M117f measured the next step of the same lesson: a SOLID 1.4 u
 *    additive strip up the centre of the boards washed the middle run
 *    near-white from 40 u up the road, which is exactly the "runway"
 *    read the owner rejected. Incline decals now use a MAP (worn hazard
 *    tape, streaky sheen) at low opacity, still at the incline-decal
 *    rotation −π/2 − atan(slope) (the negated form buries it in the ramp).
 *  - Every SOLID-looking works prop stands OUTSIDE the run-up lane, but the
 *    yard still has to be SEEN. Measured on this course: the ramp occupies
 *    road lateral 4..10 and the guardrail is at 18, so the yard lives in
 *    the dead outer lane at 4.5..9.5 off the ramp centre (road lateral
 *    11.5..16.5 — no racing line uses it, the bulky items sit at 7.4+ =
 *    4.4 u clear of the ramp edge, and the whole cluster funnels you onto
 *    the boards the way the NYC works taper does). The first pass put the
 *    mixer/sandbags/tubes BEYOND the rail and the compound VANISHED from
 *    the driver's approach — the tufa parapet swallows anything under
 *    ~1.5 u, and everything taller was hidden behind the hoarding. Only
 *    the hoarding line and the spoil heap stay outside the rail now.
 *  - The yard also has to sit AT the ramp foot: props past a ≈ +2 along
 *    the run hide behind the ramp itself on the approach (measured), so
 *    the whole cluster is packed into a ∈ [−6, +2].
 *  - Window glow is a windows-only emissiveMap whisper (0.55 on black),
 *    never a whole-texture wash — at Napoli's noon it disappears politely
 *    and only warms the shaded facades.
 */

// ---- Napoli colour language (ties to scenery/palettes + zones/napoli) -----

/** Pastel house tints — mirrors scenery/palettes NAPOLI_PASTEL (duplicated
 *  locally: this file may not import from shared scenery files, per the
 *  batch-2 no-merge-conflict rule). */
const PASTELS = [0xe07a52, 0xeab35c, 0xdf9d8a, 0xf2e3bc, 0xeacb70, 0xd06a4c, 0xe2ad66];
/** Laundry colours — mirrors builders.ts CLOTH_COLORS (same duplication rule). */
const CLOTHS = [0xe8e2d2, 0x4a90c8, 0xd8563a, 0xe0c838, 0x6aa84f];
const NAP = {
  shutter: 0x2e6a48, // the spaccanapoli zone's green shutters
  iron: 0x2e3138, // balcony/aerial iron (the zone's rail mass colour)
  steel: 0x3a3e44, // scaffold tube
  timber: 0x8a6a44, // props / toe boards
  terra: 0xb3542e, // terracotta (pots, coping, shards)
  rubble: 0x9a8a70, // tufa rubble at the collapsed exit
  accent: 0xffc978, // warm lantern gold (the parapet glow line)
  // (M117f: the series' cool boost tint is gone from this launcher — the
  // go-fast cue is worn hazard tape + a damp sheen, never a lit strip.)
};

/** Parapet height over the deck floor (a stucco kerb, not a steel rail). */
const PARA_H = 0.85;
/** How far the roof plane continues PAST the parapet — the outer band where
 *  the chimneys/tanks/aerials live, so props never float off the edge. */
const ROOF_OVERHANG = 1.45;
/** Constant height of the painted-window facade band (2 floors). */
const FACADE_H = 3.15;
/** Constant height of the painted GROUND-FLOOR band (doors, shuttered low
 *  windows, plinth). Stands ON the ground, 0.3 of it buried so a sloping
 *  terrain sample can never open a gap under the doorstep. */
const STREET_H = 2.1;
/** The MEZZANINE band's height window (M117g). MEASURED: the filler it takes
 *  its slice from runs 1.2–2.8 u on the tour seed and up to 5.6 on seed 123, so
 *  a fixed height would either never fit or leave most of the filler blank. The
 *  band is clamped instead — the spread puts its windows at 0.42–0.80 u, the
 *  range a real ammezzato light covers — and below MEZZ_MIN the storey is
 *  dropped whole (never squashed).
 *  MEASURED AGAIN after the first pass: at MEZZ_MIN 1.15 the band missed the
 *  most-looked-at stretch by 0.04 u (the filler there is 1.19), so the money
 *  shot still showed a blank band. 1.0 catches it — coverage below. */
const MEZZ_MIN = 1.0;
const MEZZ_MAX = 1.9;

/** THE BAY GUARD, shared by every builder that plants something on the ground
 *  under the ride (MEASURED): the block interior the deck crosses is partly
 *  SEA — the lungomare's water sheets (bayfront 0.30..0.62, on the shortcut's
 *  own side) reach inside the road loop. Anything grounded there stands in the
 *  water. 12% of the deck skirt sits over it on the tour seed. */
const BAY_FROM = 0.29;
const BAY_TO = 0.63;
function overBay(track: Track, sc: PlacedShortcut, x: number, z: number): boolean {
  // Hint-less project = full scan (the M99 rule: a hinted project reads
  // clamped bogus arcs for anything far off the road).
  const pr = track.project(x, z);
  const frac = pr.distance / track.finishDistance;
  return frac > BAY_FROM && frac < BAY_TO && Math.sign(pr.lateral) === sc.door.side;
}
/** Over the last stretch before the exit the parapet crest breaks down
 *  ("the collapsed roof") — fade length in units. */
const CREST_FADE = 16;

/**
 * THE ROOFTOP PITCH — placement is the whole ask, so every number here was
 * MEASURED against this course rather than guessed (M117f):
 *  - `side` +1 is the OUTSIDE of the deck's long left-hand sweep (heading
 *    runs +0.09 → −0.29 rad over the ride, so the path curves toward the
 *    −1 side and the camera's tangent points at +1). Probed in-engine with
 *    the real chase camera: from a car at d=120 a point 20 u out on +1
 *    projects to screen x −0.78 while the mirror point on −1 sits at +0.88
 *    — the +1 side is always nearer frame centre. (§6.2 corollary: world
 *    +X renders on the LEFT, so +1 is the screen-LEFT side.)
 *  - `at` 145 with `off` 20 keeps the pitch inside the frame for the whole
 *    approach: screen x runs −0.22 (car at d=70) → −0.29 → −0.37 → −0.47
 *    → −0.65 (d=150), i.e. ~80 u ≈ 2 s of centred read before you draw
 *    alongside, on the pane's NARROW 0.89 aspect (a landscape phone is
 *    wider still). Pushing it to off 26 walks it to −0.83 by d=150.
 *  - The stretch is the ride's straightest (≈4° of total heading change
 *    over 50 u, vs 47° through the entry bend), it clears the landing
 *    (d 0..30) and the chute (d > 260), and the ground there is dry and
 *    empty — nearest water sheet 100 u away, nearest zone building 79 u
 *    (measured headlessly over the real placement).
 *  - `drop` is measured off the LOWEST deck node over the span, so the
 *    terrace is below the ride everywhere (you look DOWN onto the game)
 *    even though the deck falls ~1 u across the pitch's length.
 */
const PITCH = {
  at: 145,
  off: 20,
  side: 1 as const,
  /** Playing area: along the ride × across (five-a-side proportions). */
  long: 30,
  wide: 17,
  /** Terrace margin around the playing area (bench, floodlights, cage). */
  margin: 2.4,
  /** Playing surface below the lowest deck floor over the span. */
  drop: 1.0,
  /** Cage fence height above the turf. */
  cage: 3.3,
};

// ---- Canvas painters ------------------------------------------------------

/** Terracotta barrel-tile terrace: tile courses run ALONG the ride (canvas
 *  x = along deck, y = across — the batch-1 convention), with valley
 *  shadows, crown highlights, staggered tile lips, moss and repairs. */
function tileTexture(): THREE.CanvasTexture {
  return canvasTex(128, 128, (x) => {
    x.fillStyle = '#b3542e';
    x.fillRect(0, 0, 128, 128);
    const BAND = 8; // one barrel course ≈ 0.61 u across the 9.9 u deck
    for (let b = 0; b < 16; b++) {
      const y0 = b * BAND;
      const t = art(b * 7 + 1);
      x.fillStyle = `rgba(${168 + t * 44},${74 + t * 26},${40 + t * 16},0.85)`;
      x.fillRect(0, y0, 128, BAND);
      x.fillStyle = 'rgba(70,32,18,0.55)'; // valley shadow
      x.fillRect(0, y0, 128, 1.5);
      x.fillRect(0, y0 + BAND - 1.5, 128, 1.5);
      x.fillStyle = 'rgba(255,205,160,0.26)'; // crown highlight
      x.fillRect(0, y0 + 3, 128, 1.6);
      const off = Math.floor(art(b * 13 + 3) * 24); // staggered tile lips
      for (let gx = -24; gx < 128; gx += 24) {
        x.fillStyle = 'rgba(70,32,18,0.5)';
        x.fillRect(gx + off, y0 + 1, 2, BAND - 2);
        x.fillStyle = 'rgba(240,180,130,0.34)';
        x.fillRect(gx + off + 2, y0 + 1, 1.5, BAND - 2);
      }
    }
    for (let i = 0; i < 10; i++) {
      // moss + weather blotches
      x.fillStyle = `rgba(${74 + art(i * 3) * 30},${104 + art(i * 5) * 28},${52 + art(i * 7) * 20},${0.13 + art(i * 11) * 0.15})`;
      x.fillRect(art(i * 17) * 128, art(i * 19) * 128, 4 + art(i * 23) * 10, 3 + art(i * 29) * 6);
    }
    for (let i = 0; i < 3; i++) {
      // repair patches: re-tiled runs (bright new cotto / a grey cement smear)
      const y0 = Math.floor(art(i * 31 + 5) * 16) * 8;
      x.fillStyle = i === 1 ? 'rgba(176,168,152,0.9)' : 'rgba(214,124,72,0.9)';
      x.fillRect(art(i * 37 + 5) * 100, y0 + 1, 14 + art(i * 41) * 16, 6);
    }
  });
}

/** Near-white cream stucco (material `color` tints it — towers reuse it). */
function plasterTexture(): THREE.CanvasTexture {
  return canvasTex(64, 64, (x) => {
    x.fillStyle = '#efe9db';
    x.fillRect(0, 0, 64, 64);
    for (let i = 0; i < 18; i++) {
      const g2 = 205 + art(i) * 36;
      x.fillStyle = `rgba(${g2},${g2 - 6},${g2 - 20},${0.16 + art(i * 7) * 0.2})`;
      x.fillRect(art(i * 11) * 64, art(i * 13) * 64, 5 + art(i * 17) * 16, 3 + art(i * 19) * 9);
    }
    for (let i = 0; i < 6; i++) {
      // weather streaks
      x.fillStyle = `rgba(120,108,88,${0.1 + art(i * 23) * 0.1})`;
      x.fillRect(art(i * 29) * 64, 0, 1.5 + art(i * 31) * 2, 64);
    }
  });
}

/** Grimier ochre plaster for the facade base filler + the entry gable —
 *  featureless on purpose (this band absorbs all the height stretch). */
function grimyPlasterTexture(): THREE.CanvasTexture {
  return canvasTex(64, 64, (x) => {
    // Warm ochre, mottled WARM (a grey-leaning base read as a concrete
    // podium under the pastel row — measured from the block-side view).
    x.fillStyle = '#c0a87f';
    x.fillRect(0, 0, 64, 64);
    for (let i = 0; i < 16; i++) {
      const g2 = 170 + art(i * 5) * 38;
      x.fillStyle = `rgba(${g2},${g2 - 22},${g2 - 54},${0.16 + art(i * 7) * 0.18})`;
      x.fillRect(art(i * 11) * 64, art(i * 13) * 64, 6 + art(i * 17) * 18, 4 + art(i * 19) * 10);
    }
    x.fillStyle = 'rgba(86,72,52,0.26)'; // rising damp at street level
    x.fillRect(0, 52, 64, 12);
    for (let i = 0; i < 4; i++) {
      x.fillStyle = `rgba(110,92,64,${0.09 + art(i * 23) * 0.09})`;
      x.fillRect(art(i * 29) * 64, 0, 2 + art(i * 31) * 2, 64);
    }
  });
}

/** SANPIETRINI — the basalt setts every Neapolitan courtyard and alley is laid
 *  with (the same language as the city's 'cobble' roadStyle, one octave larger
 *  so it reads from deck height). Tiled by REAL size in buildCourtyard. */
function paveTexture(): THREE.CanvasTexture {
  const tex = canvasTex(128, 128, (x) => {
    x.fillStyle = '#4c4a48';
    x.fillRect(0, 0, 128, 128);
    for (let row = 0; row < 16; row++) {
      const off = (row % 2) * 4; // staggered courses
      for (let col = 0; col < 16; col++) {
        const i = row * 16 + col;
        const g = 66 + art(i * 3) * 34;
        x.fillStyle = `rgb(${g},${g - 2 + art(i * 5) * 5},${g + 2 + art(i * 7) * 5})`;
        x.fillRect(off + col * 8 + 0.6, row * 8 + 0.6, 6.8, 6.8);
      }
    }
    // Damp seams + the worn polish a century of feet leaves down the middle.
    for (let i = 0; i < 22; i++) {
      x.fillStyle = `rgba(28,28,30,${0.1 + art(i * 11) * 0.16})`;
      x.fillRect(art(i * 13) * 128, art(i * 17) * 128, 10 + art(i * 19) * 30, 1.6);
    }
    for (let i = 0; i < 10; i++) {
      x.fillStyle = `rgba(184,180,170,${0.05 + art(i * 23) * 0.09})`;
      x.beginPath();
      x.ellipse(art(i * 29) * 128, art(i * 31) * 128, 8 + art(i * 37) * 18, 5 + art(i * 41) * 10, 0, 0, 6.3);
      x.fill();
    }
  });
  tex.anisotropy = 8; // a ground plane seen almost edge-on from the deck
  return tex;
}

/** One facade repeat = FOUR pastel house fronts (≈6.5 u each along the ride):
 *  cornice, two floors of shuttered windows, iron balconies, sills, grime,
 *  stone base. `glow` paints the matching windows-only emissive layer. */
function paintFacades(x: CanvasRenderingContext2D, glow: boolean): void {
  if (glow) {
    x.fillStyle = '#000';
    x.fillRect(0, 0, 512, 128);
  }
  for (let hIdx = 0; hIdx < 4; hIdx++) {
    const x0 = hIdx * 128;
    if (!glow) {
      const c = PASTELS[Math.floor(art(hIdx * 3 + 2) * PASTELS.length) % PASTELS.length];
      x.fillStyle = `rgb(${(c >> 16) & 255},${(c >> 8) & 255},${c & 255})`;
      x.fillRect(x0, 0, 128, 128);
      for (let i = 0; i < 8; i++) {
        // sun-bleached plaster mottle
        x.fillStyle = `rgba(255,244,224,${0.08 + art(hIdx * 17 + i) * 0.12})`;
        x.fillRect(x0 + art(hIdx * 7 + i * 3) * 118, art(hIdx * 11 + i * 5) * 118, 6 + art(i * 13) * 20, 4 + art(i * 19) * 10);
      }
      x.fillStyle = 'rgba(60,48,40,0.85)'; // cornice under the roof edge
      x.fillRect(x0, 0, 128, 5);
      x.fillStyle = 'rgba(150,134,108,0.9)'; // stone base course
      x.fillRect(x0, 120, 128, 8);
      x.fillStyle = 'rgba(74,60,50,0.5)'; // party-wall seam
      x.fillRect(x0 + 126, 0, 2, 128);
    }
    for (const [row, yW] of [
      [0, 16],
      [1, 70],
    ] as const) {
      for (const wx of [26, 78]) {
        const wxx = x0 + wx;
        const lit = art(hIdx * 29 + row * 7 + wx) < 0.5;
        if (glow) {
          if (lit) {
            x.fillStyle = 'rgba(255,182,104,1)';
            x.fillRect(wxx + 2, yW + 3, 22, 34);
          }
          continue;
        }
        x.fillStyle = '#242b30'; // glass
        x.fillRect(wxx, yW, 26, 40);
        x.fillStyle = 'rgba(120,140,160,0.35)'; // sky catch
        x.fillRect(wxx + 3, yW + 3, 8, 34);
        x.fillStyle = '#2e6a48'; // shutter pairs (the zone's green)
        x.fillRect(wxx - 8, yW, 7, 40);
        x.fillRect(wxx + 27, yW, 7, 40);
        x.fillStyle = 'rgba(230,220,200,0.9)'; // sill
        x.fillRect(wxx - 9, yW + 40, 44, 3);
        x.fillStyle = 'rgba(96,80,64,0.25)'; // grime streak under the sill
        x.fillRect(wxx - 2, yW + 43, 30, 12);
        if (row === 1 && art(hIdx * 41 + wx) < 0.55) {
          // iron balcony on some lower windows: slab + rail + balusters
          x.fillStyle = '#23262b';
          x.fillRect(wxx - 10, yW + 42, 46, 4);
          x.fillStyle = 'rgba(46,49,56,0.9)';
          x.fillRect(wxx - 10, yW + 28, 46, 2);
          for (let bp = 0; bp <= 44; bp += 6) x.fillRect(wxx - 10 + bp, yW + 28, 1.6, 16);
        }
      }
    }
  }
}

/** GROUND FLOOR — the same FOUR house fronts, at street level: a stone plinth,
 *  an arched doorway with a step, a shuttered low window, a wall lamp and the
 *  damp rising off the alley. Painted as its own CONSTANT-height band anchored
 *  at the GROUND (see the band solver in buildRooftopRun) so nothing here ever
 *  stretches — the variable-height grimy filler above absorbs all of it. House
 *  tints use the SAME art() index as paintFacades, so a door always lands under
 *  its own windows (both bands ride uvStep 26 = 4 houses per 26 u). */
function paintStreetLevel(x: CanvasRenderingContext2D): void {
  for (let hIdx = 0; hIdx < 4; hIdx++) {
    const x0 = hIdx * 128;
    const c = PASTELS[Math.floor(art(hIdx * 3 + 2) * PASTELS.length) % PASTELS.length];
    x.fillStyle = `rgb(${(c >> 16) & 255},${(c >> 8) & 255},${c & 255})`;
    x.fillRect(x0, 0, 128, 128);
    for (let i = 0; i < 6; i++) {
      x.fillStyle = `rgba(255,244,224,${0.06 + art(hIdx * 19 + i) * 0.1})`;
      x.fillRect(x0 + art(hIdx * 5 + i * 3) * 118, art(hIdx * 13 + i * 7) * 100, 8 + art(i * 11) * 22, 4 + art(i * 17) * 9);
    }
    // Damp rising off the alley + the greasy scuff band hands and crates leave.
    const damp = x.createLinearGradient(0, 128, 0, 74);
    damp.addColorStop(0, 'rgba(64,58,46,0.5)');
    damp.addColorStop(1, 'rgba(64,58,46,0)');
    x.fillStyle = damp;
    x.fillRect(x0, 74, 128, 54);
    x.fillStyle = 'rgba(150,134,108,0.92)'; // basalt plinth course
    x.fillRect(x0, 116, 128, 12);
    x.fillStyle = 'rgba(74,60,50,0.5)'; // party-wall seam
    x.fillRect(x0 + 126, 0, 2, 128);

    // The doorway: recessed jamb, dark opening, arched head, worn stone step.
    const dx = x0 + 18 + Math.floor(art(hIdx * 7 + 5) * 10);
    x.fillStyle = 'rgba(146,128,102,0.95)'; // stone surround
    x.fillRect(dx - 4, 52, 38, 76);
    x.fillStyle = '#20242a'; // opening
    x.fillRect(dx, 58, 30, 70);
    x.beginPath(); // arched head
    x.fillStyle = '#20242a';
    x.ellipse(dx + 15, 58, 15, 9, 0, Math.PI, 0);
    x.fill();
    if (art(hIdx * 11 + 3) < 0.6) {
      x.fillStyle = '#2f4a38'; // a green-painted door leaf standing ajar
      x.fillRect(dx + 1, 60, 13, 66);
      x.fillStyle = 'rgba(255,255,255,0.10)';
      x.fillRect(dx + 3, 66, 9, 26);
    }
    x.fillStyle = 'rgba(198,186,164,0.95)'; // step
    x.fillRect(dx - 6, 124, 42, 4);

    // A low shuttered window beside the door (the zone's green shutters).
    const wx = x0 + 74 + Math.floor(art(hIdx * 23) * 8);
    x.fillStyle = '#242b30';
    x.fillRect(wx, 62, 24, 34);
    x.fillStyle = 'rgba(120,140,160,0.3)';
    x.fillRect(wx + 3, 65, 7, 28);
    x.fillStyle = '#2e6a48';
    x.fillRect(wx - 7, 62, 6, 34);
    x.fillRect(wx + 25, 62, 6, 34);
    x.fillStyle = 'rgba(46,49,56,0.85)'; // window grille bars
    for (let b = 0; b <= 24; b += 6) x.fillRect(wx + b, 62, 1.4, 34);
    x.fillStyle = 'rgba(230,220,200,0.9)'; // sill
    x.fillRect(wx - 8, 96, 40, 3);
    if (art(hIdx * 29 + 1) < 0.5) {
      x.fillStyle = 'rgba(40,42,48,0.85)'; // wall lamp bracket
      x.fillRect(x0 + 108, 40, 3, 12);
      x.fillStyle = 'rgba(250,226,178,0.85)';
      x.fillRect(x0 + 104, 36, 11, 6);
    }
  }
}

/** THE MEZZANINE — the skirt's THIRD real storey (QA: deck-skirt-street,
 *  deck-skirt-close). Between the window band and the ground floor sat a
 *  featureless filler that absorbs the deck's height change; at street level it
 *  was the most prominent thing on the block, so the run read as
 *  "houses / retaining wall / shops" instead of one block of houses.
 *
 *  A mezzanine is the architecturally true answer AND the one that fits: the
 *  measured filler is 1.2–2.8 u on the tour seed (up to 5.6 on seed 123), which
 *  is a Neapolitan ammezzato — short grilled windows under the piano nobile —
 *  and NOT a full 3 u storey. The band solver in buildRooftopRun clamps its
 *  height to MEZZ_MIN..MEZZ_MAX (a 1.6× spread = window heights 0.5..0.8 u,
 *  both plausible) and DROPS the storey entirely below MEZZ_MIN rather than
 *  squashing it — the same "collapse, never squash" rule the window band uses.
 *  Houses ride the SAME art() tint and the same uvStep 26 as the window and
 *  ground-floor bands, so a mezzanine window always lands on its own house. */
function paintMezzanine(x: CanvasRenderingContext2D): void {
  for (let hIdx = 0; hIdx < 4; hIdx++) {
    const x0 = hIdx * 128;
    const c = PASTELS[Math.floor(art(hIdx * 3 + 2) * PASTELS.length) % PASTELS.length];
    x.fillStyle = `rgb(${(c >> 16) & 255},${(c >> 8) & 255},${c & 255})`;
    x.fillRect(x0, 0, 128, 128);
    for (let i = 0; i < 6; i++) {
      x.fillStyle = `rgba(255,244,224,${0.06 + art(hIdx * 23 + i) * 0.11})`;
      x.fillRect(x0 + art(hIdx * 7 + i * 3) * 118, art(hIdx * 11 + i * 5) * 112, 7 + art(i * 13) * 20, 4 + art(i * 17) * 9);
    }
    // The two marcapiano string courses that bracket a mezzanine.
    x.fillStyle = 'rgba(236,228,208,0.92)';
    x.fillRect(x0, 0, 128, 8);
    x.fillStyle = 'rgba(74,60,50,0.32)';
    x.fillRect(x0, 8, 128, 2);
    x.fillStyle = 'rgba(214,202,178,0.85)';
    x.fillRect(x0, 118, 128, 10);
    x.fillStyle = 'rgba(74,60,50,0.5)'; // party-wall seam
    x.fillRect(x0 + 126, 0, 2, 128);
    // Two short grilled windows per house, aligned under the floor above.
    for (const wx of [24, 76]) {
      const wxx = x0 + wx;
      x.fillStyle = 'rgba(146,128,102,0.85)'; // stone surround
      x.fillRect(wxx - 4, 28, 36, 62);
      x.fillStyle = '#242b30'; // glass
      x.fillRect(wxx, 32, 28, 54);
      x.fillStyle = 'rgba(120,140,160,0.32)'; // sky catch
      x.fillRect(wxx + 3, 35, 8, 46);
      x.fillStyle = 'rgba(46,49,56,0.9)'; // the grille a low window always wears
      for (let b = 0; b <= 28; b += 6) x.fillRect(wxx + b, 32, 1.5, 54);
      x.fillRect(wxx, 56, 28, 1.5);
      if (art(hIdx * 37 + wx) < 0.55) {
        x.fillStyle = '#2e6a48'; // one shutter leaf pushed back against the wall
        x.fillRect(wxx - 9, 32, 6, 54);
      }
      x.fillStyle = 'rgba(230,220,200,0.9)'; // sill
      x.fillRect(wxx - 6, 88, 40, 4);
      x.fillStyle = 'rgba(96,80,64,0.22)'; // grime off the sill
      x.fillRect(wxx, 92, 26, 14);
    }
  }
}

/** THE BASE WALL — what is left of the filler once the mezzanine has taken its
 *  slice. It still has to absorb ALL the remaining height variation (0 u to
 *  ~3.8 u), so it carries only stretch-TOLERANT paint: the house's own pastel,
 *  vertical downpipes and weather streaks (vertical detail is immune to a
 *  vertical stretch), a gradient of rising damp, and string/plinth courses thin
 *  enough to read at any height. The old band was a grey-leaning ochre with no
 *  house identity at all, which is precisely why it read as a retaining wall —
 *  this one is the SAME house tint as the two floors above it, so the wall
 *  simply continues down. */
function paintBaseWall(x: CanvasRenderingContext2D): void {
  for (let hIdx = 0; hIdx < 4; hIdx++) {
    const x0 = hIdx * 128;
    const c = PASTELS[Math.floor(art(hIdx * 3 + 2) * PASTELS.length) % PASTELS.length];
    x.fillStyle = `rgb(${(c >> 16) & 255},${(c >> 8) & 255},${c & 255})`;
    x.fillRect(x0, 0, 128, 128);
    for (let i = 0; i < 10; i++) {
      const g2 = 196 + art(hIdx * 5 + i * 3) * 48;
      x.fillStyle = `rgba(${g2},${g2 - 16},${g2 - 40},${0.1 + art(i * 7) * 0.16})`;
      x.fillRect(art(hIdx * 11 + i * 13) * 118, art(hIdx * 17 + i * 19) * 118, 8 + art(i * 23) * 22, 5 + art(i * 29) * 14);
    }
    x.fillStyle = 'rgba(236,228,208,0.85)'; // string course under the floor above
    x.fillRect(x0, 0, 128, 5);
    // Rising damp off the alley — a gradient stretches gracefully at any height.
    const damp = x.createLinearGradient(0, 128, 0, 60);
    damp.addColorStop(0, 'rgba(74,64,48,0.45)');
    damp.addColorStop(1, 'rgba(74,64,48,0)');
    x.fillStyle = damp;
    x.fillRect(x0, 60, 128, 68);
    for (let i = 0; i < 3; i++) {
      // weather streaks (vertical: stretch-proof)
      x.fillStyle = `rgba(112,94,68,${0.08 + art(hIdx * 31 + i * 5) * 0.1})`;
      x.fillRect(x0 + 8 + art(hIdx * 41 + i * 7) * 104, 0, 2 + art(i * 11) * 3, 128);
    }
    if (art(hIdx * 43 + 9) < 0.6) {
      x.fillStyle = 'rgba(52,46,40,0.9)'; // cast-iron downpipe
      x.fillRect(x0 + 106 + art(hIdx * 47) * 12, 0, 5, 128);
    }
    x.fillStyle = 'rgba(74,60,50,0.5)'; // party-wall seam
    x.fillRect(x0 + 126, 0, 2, 128);
  }
}

function facadeTexture(): THREE.CanvasTexture {
  return canvasTex(512, 128, (x) => paintFacades(x, false));
}

function mezzTexture(): THREE.CanvasTexture {
  return canvasTex(512, 128, paintMezzanine);
}

function baseTexture(): THREE.CanvasTexture {
  return canvasTex(512, 128, paintBaseWall);
}

function streetTexture(): THREE.CanvasTexture {
  return canvasTex(512, 128, paintStreetLevel);
}

function facadeGlowTexture(): THREE.CanvasTexture {
  return canvasTex(512, 128, (x) => paintFacades(x, true));
}

/** Site-worn scaffold boards lying ACROSS the run (box top face: canvas y =
 *  along the run, so boards are y-bands — the launcherTexture convention).
 *  M117f: the bleached-clean version read as a showroom loading ramp, so the
 *  boards now carry cement splatter, mud tread and two replaced planks. */
function scaffoldPlankTexture(): THREE.CanvasTexture {
  // TEXEL DENSITY (QA: launcher-chase — the deck smeared into blurry diagonal
  // bands, and the aim surface must be the crispest thing on the ramp). The
  // 64² canvas mapped ONCE across a ~9.6 u board run is ~6.7 texels/u, and
  // BoxGeometry UVs give no tiling, so each painted board covered 1.2 u. Fixed
  // on all three axes of the problem: 4× the resolution (the context is scaled
  // so every drawing coordinate below stays in the measured 64-space), 3×
  // tiling along the run (see the `planks` material — boards land at a real
  // ~0.4 u), and anisotropic filtering, which is what actually kills the smear
  // at the chase camera's grazing angle. Net ≈ 80 texels/u along × 111 across.
  const tex = canvasTex(256, 256, (x) => {
    x.scale(4, 4);
    x.fillStyle = '#8f7148';
    x.fillRect(0, 0, 64, 64);
    for (let row = 0; row < 8; row++) {
      const swap = row === 2 || row === 6; // replaced boards: newer, paler
      const base = swap ? 196 : 150 + art(row) * 34;
      x.fillStyle = `rgba(${base},${base - 28 + art(row * 3) * 18},${base - 62 + art(row * 5) * 14},0.82)`;
      x.fillRect(0, row * 8, 64, 7);
      x.strokeStyle = 'rgba(58,42,24,0.8)';
      x.lineWidth = 1;
      x.beginPath();
      x.moveTo(0, row * 8 + 7.5);
      x.lineTo(64, row * 8 + 7.5);
      x.stroke();
      x.fillStyle = `rgba(96,74,44,${0.16 + art(row * 7) * 0.14})`; // grain
      x.fillRect(art(row * 11) * 40, row * 8 + 2, 18 + art(row * 13) * 20, 1.4);
      x.fillStyle = 'rgba(50,50,54,0.85)'; // nail heads at the bearers
      x.fillRect(14, row * 8 + 3, 2, 2);
      x.fillRect(48, row * 8 + 3, 2, 2);
    }
    for (let i = 0; i < 16; i++) {
      // Dried cement splashes + mud tread dragged up off the street.
      const pale = art(i * 3) < 0.55;
      x.fillStyle = pale
        ? `rgba(206,202,190,${0.2 + art(i * 5) * 0.34})`
        : `rgba(74,60,44,${0.16 + art(i * 7) * 0.24})`;
      x.beginPath();
      x.ellipse(art(i * 11) * 64, art(i * 13) * 64, 2 + art(i * 17) * 7, 1.5 + art(i * 19) * 4, art(i * 23) * 3, 0, 6.3);
      x.fill();
    }
  });
  tex.anisotropy = 8; // the codebase max (the shortcutTokyo lane-paint precedent)
  return tex;
}

/** Orange debris netting: translucent wash + mesh grid on a clear ground. */
function nettingTexture(): THREE.CanvasTexture {
  return canvasTex(64, 64, (x) => {
    x.fillStyle = 'rgba(230,96,34,0.16)';
    x.fillRect(0, 0, 64, 64);
    x.strokeStyle = 'rgba(235,104,40,0.8)';
    x.lineWidth = 1;
    for (let i = 0; i <= 64; i += 6) {
      x.beginPath();
      x.moveTo(i + 0.5, 0);
      x.lineTo(i + 0.5, 64);
      x.stroke();
      x.beginPath();
      x.moveTo(0, i + 0.5);
      x.lineTo(64, i + 0.5);
      x.stroke();
    }
  });
}

/** The LAVORI IN CORSO site board (canvas text is the M97/M116 precedent). */
function lavoriTexture(): THREE.CanvasTexture {
  const tex = canvasTex(128, 96, (x) => {
    x.fillStyle = '#e8e2d0';
    x.fillRect(0, 0, 128, 96);
    x.strokeStyle = '#c8332a';
    x.lineWidth = 7;
    x.strokeRect(3.5, 3.5, 121, 89);
    x.fillStyle = '#1e2226';
    x.font = '900 21px Arial, sans-serif';
    x.textAlign = 'center';
    x.fillText('LAVORI', 64, 40);
    x.fillText('IN CORSO', 64, 66);
    x.fillStyle = 'rgba(200,51,42,0.9)';
    x.fillRect(14, 76, 100, 6);
    for (let i = 0; i < 6; i++) {
      // weathering
      x.fillStyle = `rgba(120,110,92,${0.08 + art(i * 13) * 0.1})`;
      x.fillRect(art(i * 7) * 120, art(i * 11) * 88, 6 + art(i * 17) * 18, 4 + art(i * 19) * 8);
    }
  });
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  return tex;
}

/** Worn hazard tape laid across the boards at the lip — the ONLY painted
 *  "go" cue left on the ramp (M117f). Diagonals, chipped and scuffed, with
 *  a transparent ground so it reads as paint on timber, not a light bar. */
function hazardTexture(): THREE.CanvasTexture {
  return canvasTex(128, 32, (x) => {
    for (let i = -32; i < 160; i += 16) {
      x.save();
      x.globalAlpha = 0.62 + art(i * 3) * 0.3;
      x.fillStyle = ((i / 16) | 0) % 2 === 0 ? '#e8b52a' : '#26282c';
      x.beginPath();
      x.moveTo(i, 0);
      x.lineTo(i + 10, 0);
      x.lineTo(i + 26, 32);
      x.lineTo(i + 16, 32);
      x.closePath();
      x.fill();
      x.restore();
    }
    // Chips: tyre scuffs eating the paint back to bare board.
    for (let i = 0; i < 22; i++) {
      x.clearRect(art(i * 7) * 128, art(i * 11) * 32, 2 + art(i * 13) * 9, 1 + art(i * 17) * 5);
    }
  });
}

/** Streaky wet sheen for the boards: damp timber catching the sun. Painted
 *  as broken vertical smears on a clear ground, so at low additive opacity
 *  it reads as damp, never as a lit lane (the M117f runway lesson). */
function sheenTexture(): THREE.CanvasTexture {
  return canvasTex(64, 128, (x) => {
    for (let i = 0; i < 26; i++) {
      const w = 2 + art(i * 5) * 9;
      const h = 8 + art(i * 7) * 40;
      x.fillStyle = `rgba(200,226,246,${0.14 + art(i * 11) * 0.3})`;
      x.fillRect(art(i * 13) * 64, art(i * 17) * 128, w, h);
    }
  });
}

/** Blue PVC site tarpaulin: folds, sun-bleach, grommet eyelets, grime. */
function tarpTexture(): THREE.CanvasTexture {
  return canvasTex(64, 64, (x) => {
    x.fillStyle = '#2f6fa0';
    x.fillRect(0, 0, 64, 64);
    for (let i = 0; i < 12; i++) {
      // woven fold bands
      x.fillStyle = `rgba(${120 + art(i * 3) * 70},${170 + art(i * 5) * 50},${200 + art(i * 7) * 40},${0.1 + art(i * 11) * 0.16})`;
      x.fillRect(art(i * 13) * 64, 0, 2 + art(i * 17) * 5, 64);
    }
    for (let i = 0; i < 8; i++) {
      x.fillStyle = `rgba(180,178,160,${0.08 + art(i * 19) * 0.14})`; // cement dust
      x.fillRect(art(i * 23) * 64, art(i * 29) * 64, 5 + art(i * 31) * 16, 3 + art(i * 37) * 9);
    }
    x.fillStyle = '#3a3d42'; // grommets down one edge
    for (let gy = 5; gy < 64; gy += 12) x.fillRect(2, gy, 3, 3);
  });
}

/** Site hoarding board: OSB ply with a red/white band and faded stencil. */
function hoardingTexture(): THREE.CanvasTexture {
  const tex = canvasTex(128, 80, (x) => {
    x.fillStyle = '#b2884e';
    x.fillRect(0, 0, 128, 80);
    for (let i = 0; i < 30; i++) {
      // OSB flake chips
      x.fillStyle = `rgba(${160 + art(i * 3) * 60},${120 + art(i * 5) * 46},${70 + art(i * 7) * 34},${0.2 + art(i * 11) * 0.3})`;
      x.save();
      x.translate(art(i * 13) * 128, art(i * 17) * 80);
      x.rotate((art(i * 19) - 0.5) * 1.4);
      x.fillRect(0, 0, 6 + art(i * 23) * 16, 2 + art(i * 29) * 4);
      x.restore();
    }
    x.fillStyle = '#c8332a'; // red/white warning band
    x.fillRect(0, 52, 128, 14);
    x.fillStyle = '#e8e2d0';
    for (let i = 0; i < 128; i += 22) x.fillRect(i, 52, 11, 14);
    x.fillStyle = 'rgba(40,44,48,0.62)'; // faded stencil
    x.font = '900 14px Arial, sans-serif';
    x.textAlign = 'center';
    x.fillText('CANTIERE', 64, 26);
    x.fillText('VIETATO ACCESSO', 64, 44);
    for (let i = 0; i < 7; i++) {
      x.fillStyle = `rgba(96,86,64,${0.08 + art(i * 31) * 0.12})`;
      x.fillRect(art(i * 37) * 120, art(i * 41) * 72, 6 + art(i * 43) * 18, 4 + art(i * 47) * 9);
    }
  });
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  return tex;
}

/** Rusted chain-link: a diamond mesh on a CLEAR ground (the canvas alpha is
 *  the transparency), so the cage veils the game without hiding it. */
function chainTexture(): THREE.CanvasTexture {
  return canvasTex(64, 64, (x) => {
    x.lineWidth = 1.6;
    for (const [dir, base] of [
      [1, 'rgba(178,182,186,'],
      [-1, 'rgba(158,146,132,'],
    ] as const) {
      for (let i = -64; i <= 128; i += 10) {
        x.strokeStyle = `${base}${0.5 + art(i * 3 + (dir > 0 ? 0 : 7)) * 0.32})`;
        x.beginPath();
        x.moveTo(i, 0);
        x.lineTo(i + dir * 64, 64);
        x.stroke();
      }
    }
    for (let i = 0; i < 16; i++) {
      // rust blooms on the wire
      x.fillStyle = `rgba(150,86,44,${0.16 + art(i * 13) * 0.24})`;
      x.fillRect(art(i * 17) * 64, art(i * 19) * 64, 3 + art(i * 23) * 6, 3 + art(i * 29) * 6);
    }
  });
}

/** Goal netting: a fine white square mesh on a clear ground. */
function goalNetTexture(): THREE.CanvasTexture {
  return canvasTex(64, 64, (x) => {
    x.strokeStyle = 'rgba(238,240,242,0.72)';
    x.lineWidth = 1.2;
    for (let i = 0; i <= 64; i += 8) {
      x.beginPath();
      x.moveTo(i + 0.5, 0);
      x.lineTo(i + 0.5, 64);
      x.stroke();
      x.beginPath();
      x.moveTo(0, i + 0.5);
      x.lineTo(64, i + 0.5);
      x.stroke();
    }
  });
}

/** The five-a-side surface: worn astroturf with real markings. Canvas x runs
 *  ACROSS the pitch, y ALONG the ride (the decal convention: the plane is
 *  laid with rotation.y = dir then rotation.x = −π/2, so its local +Y — the
 *  texture's v — points down-course). Drawn at ~15 px per unit. */
function turfTexture(): THREE.CanvasTexture {
  const W = 256;
  const H = 448;
  const tex = canvasTex(W, H, (x) => {
    x.fillStyle = '#31703c';
    x.fillRect(0, 0, W, H);
    // Mown/laid turf rolls running down the pitch.
    for (let i = 0; i < 8; i++) {
      x.fillStyle = i % 2 === 0 ? 'rgba(255,255,255,0.045)' : 'rgba(0,0,0,0.055)';
      x.fillRect(i * 32, 0, 32, H);
    }
    // Wear: bald olive/brown patches, heaviest in the goalmouths + centre.
    for (let i = 0; i < 34; i++) {
      const cy = art(i * 7) < 0.45 ? (art(i * 11) < 0.5 ? 44 : H - 44) : H / 2 + (art(i * 13) - 0.5) * 150;
      x.fillStyle = `rgba(${118 + art(i * 17) * 44},${104 + art(i * 19) * 34},${58 + art(i * 23) * 26},${0.1 + art(i * 29) * 0.2})`;
      x.beginPath();
      x.ellipse(art(i * 31) * W, cy + (art(i * 37) - 0.5) * 60, 8 + art(i * 41) * 22, 6 + art(i * 43) * 16, art(i * 47) * 3, 0, 6.3);
      x.fill();
    }
    // Markings. Weight 4 px ≈ 0.27 u so they read from the deck 20 u away.
    x.strokeStyle = 'rgba(240,244,246,0.9)';
    x.lineWidth = 4;
    const M = 9; // touchline inset
    x.strokeRect(M, M, W - M * 2, H - M * 2);
    x.beginPath(); // halfway line
    x.moveTo(M, H / 2);
    x.lineTo(W - M, H / 2);
    x.stroke();
    x.beginPath(); // centre circle + spot
    x.arc(W / 2, H / 2, 44, 0, 6.3);
    x.stroke();
    x.fillStyle = 'rgba(240,244,246,0.9)';
    x.beginPath();
    x.arc(W / 2, H / 2, 4, 0, 6.3);
    x.fill();
    for (const end of [0, 1]) {
      const y0 = end === 0 ? M : H - M;
      const sgn = end === 0 ? 1 : -1;
      x.strokeRect(W / 2 - 74, y0, 148, sgn * 62); // penalty area
      x.strokeRect(W / 2 - 38, y0, 76, sgn * 26); // goal area
      x.beginPath(); // penalty spot + the D
      x.arc(W / 2, y0 + sgn * 44, 3.5, 0, 6.3);
      x.fill();
      x.beginPath();
      x.arc(W / 2, y0 + sgn * 44, 34, end === 0 ? 0.35 : 3.49, end === 0 ? 2.79 : 5.93);
      x.stroke();
      for (const cx2 of [M, W - M]) {
        x.beginPath(); // corner arcs
        x.arc(cx2, y0, 11, 0, 6.3);
        x.stroke();
      }
    }
  });
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  return tex;
}

// ---- Merge helper ---------------------------------------------------------

interface BoxPart {
  w: number;
  h: number;
  d: number;
  x: number;
  y: number;
  z: number;
  yaw?: number;
  pitch?: number;
  roll?: number;
}

/** Merge a pile of oriented boxes into ONE mesh — the works yard and the
 *  pitch's metalwork are dozens of little sticks; welded they cost a single
 *  draw call each (the finishPlaza precedent). */
function weld(parts: readonly BoxPart[], material: THREE.Material): THREE.Mesh {
  const o = new THREE.Object3D();
  const geos = parts.map((p) => {
    const gg = new THREE.BoxGeometry(p.w, p.h, p.d);
    o.position.set(p.x, p.y, p.z);
    o.rotation.order = 'YXZ';
    o.rotation.set(p.pitch ?? 0, p.yaw ?? 0, p.roll ?? 0);
    o.updateMatrix();
    gg.applyMatrix4(o.matrix);
    return gg;
  });
  const merged = mergeGeometries(geos, false) as THREE.BufferGeometry;
  for (const gg of geos) gg.dispose();
  const mesh = new THREE.Mesh(merged, material);
  mesh.matrixAutoUpdate = false;
  return mesh;
}

/** Shuttered openings on the four faces of a yaw-oriented box (M117g). The
 *  block's free-standing volumes — the laundry-span towers and the quarter's
 *  houses — were untextured plaster boxes, which is what made them read as
 *  pillars and containers rather than buildings (QA: deck-skirt-street,
 *  block-courtyard). Parts go into three shared arrays and the CALLER welds
 *  them once, so a whole quarter of openings costs three draw calls.
 *
 *  §6.1: with rotation.y = yaw a box's local +X is world (cos yaw, −sin yaw)
 *  and its local +Z is (sin yaw, cos yaw) — `halfX`/`halfZ` are the half sizes
 *  along those two axes, so a face's window boxes are thin along the face
 *  normal and wide across it. */
interface OpenParts {
  glass: BoxPart[];
  shutters: BoxPart[];
  stone: BoxPart[];
}

function boxOpenings(
  out: OpenParts,
  cx: number,
  cz: number,
  yaw: number,
  halfX: number,
  halfZ: number,
  floors: readonly number[],
  seed: number,
  doorY?: number,
): void {
  const rx = Math.cos(yaw);
  const rz = -Math.sin(yaw);
  const fx = Math.sin(yaw);
  const fz = Math.cos(yaw);
  const faces = [
    { nx: rx, nz: rz, half: halfX, tx: fx, tz: fz, span: halfZ, thinX: true },
    { nx: -rx, nz: -rz, half: halfX, tx: fx, tz: fz, span: halfZ, thinX: true },
    { nx: fx, nz: fz, half: halfZ, tx: rx, tz: rz, span: halfX, thinX: false },
    { nx: -fx, nz: -fz, half: halfZ, tx: rx, tz: rz, span: halfX, thinX: false },
  ] as const;
  const dims = (wide: number, h: number, thin: number, thinX: boolean) =>
    thinX ? { w: thin, h, d: wide } : { w: wide, h, d: thin };
  for (let f = 0; f < faces.length; f++) {
    const fc = faces[f];
    const px = cx + fc.nx * (fc.half + 0.05);
    const pz = cz + fc.nz * (fc.half + 0.05);
    const bays = Math.max(1, Math.min(3, Math.floor((fc.span * 2) / 3.0)));
    for (let fl = 0; fl < floors.length; fl++) {
      for (let b = 0; b < bays; b++) {
        // A few blank bays: a terrace with an opening in every slot reads
        // stamped, and real party walls carry dead panels.
        if (art(seed + f * 13 + fl * 7 + b * 29) < 0.16) continue;
        const o = bays === 1 ? 0 : (b / (bays - 1) - 0.5) * (fc.span * 2 - 1.9);
        const y = floors[fl];
        out.glass.push({ ...dims(0.95, 1.35, 0.12, fc.thinX), x: px + fc.tx * o, y, z: pz + fc.tz * o, yaw });
        out.stone.push({ ...dims(1.3, 0.11, 0.22, fc.thinX), x: px + fc.tx * o, y: y - 0.76, z: pz + fc.tz * o, yaw });
        for (const sh of [-1, 1] as const)
          out.shutters.push({
            ...dims(0.26, 1.35, 0.16, fc.thinX),
            x: px + fc.tx * (o + sh * 0.66),
            y,
            z: pz + fc.tz * (o + sh * 0.66),
            yaw,
          });
      }
    }
    if (doorY != null && (f === 1 || f === 3)) {
      out.glass.push({ ...dims(0.95, 1.9, 0.12, fc.thinX), x: px, y: doorY, z: pz, yaw });
      out.stone.push({ ...dims(1.35, 0.14, 0.26, fc.thinX), x: px, y: doorY + 1.0, z: pz, yaw });
    }
  }
}

/** A vertical quad between two ground points, UV'd by REAL size so a mesh
 *  texture keeps a constant wire gauge on long and short panels alike. */
function wallQuad(
  x1: number,
  z1: number,
  x2: number,
  z2: number,
  y0: number,
  h: number,
  tile: number,
): THREE.BufferGeometry {
  const len = Math.hypot(x2 - x1, z2 - z1);
  const gg = new THREE.BufferGeometry();
  gg.setAttribute(
    'position',
    new THREE.Float32BufferAttribute([x1, y0, z1, x2, y0, z2, x1, y0 + h, z1, x2, y0 + h, z2], 3),
  );
  gg.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, len / tile, 0, 0, h / tile, len / tile, h / tile], 2));
  gg.setIndex([0, 1, 2, 1, 3, 2]);
  gg.computeVertexNormals();
  return gg;
}

// ---- Shared material kit --------------------------------------------------

interface Mats {
  tile: THREE.MeshStandardMaterial;
  plaster: THREE.MeshStandardMaterial;
  grimy: THREE.MeshStandardMaterial;
  facade: THREE.MeshStandardMaterial;
  street: THREE.MeshStandardMaterial;
  mezz: THREE.MeshStandardMaterial;
  base: THREE.MeshStandardMaterial;
  pave: THREE.MeshStandardMaterial;
  terra: THREE.MeshStandardMaterial;
  cotto: THREE.MeshStandardMaterial;
  iron: THREE.MeshStandardMaterial;
  steel: THREE.MeshStandardMaterial;
  timber: THREE.MeshStandardMaterial;
  rubble: THREE.MeshStandardMaterial;
  tank: THREE.MeshStandardMaterial;
  shutter: THREE.MeshStandardMaterial;
  /** Dimmed plaster for sills, lintels and yard flagstones. MEASURED: the
   *  near-white `plaster` read as glaring white dashes on the facades and as a
   *  lit slab on the sanpietrini (QA: yard-props-close) — trim wants to be
   *  weathered stone, not stucco. Shares plaster's map, tinted down. */
  trim: THREE.MeshStandardMaterial;
  towers: THREE.MeshStandardMaterial[];
  cloths: THREE.MeshStandardMaterial[];
  // ---- M117f: the works yard + the rooftop pitch ----
  planks: THREE.MeshStandardMaterial;
  hazard: THREE.MeshBasicMaterial;
  sheen: THREE.MeshBasicMaterial;
  tarp: THREE.MeshStandardMaterial;
  hoard: THREE.MeshStandardMaterial;
  sand: THREE.MeshStandardMaterial;
  concrete: THREE.MeshStandardMaterial;
  turf: THREE.MeshStandardMaterial;
  chain: THREE.MeshStandardMaterial;
  goalNet: THREE.MeshStandardMaterial;
  white: THREE.MeshStandardMaterial;
  lamp: THREE.MeshStandardMaterial;
  ball: THREE.MeshStandardMaterial;
}

function makeMats(): Mats {
  const tile = new THREE.MeshStandardMaterial({ map: tileTexture(), roughness: 0.94, side: THREE.DoubleSide });
  const plaster = new THREE.MeshStandardMaterial({ map: plasterTexture(), roughness: 0.95, side: THREE.DoubleSide });
  const grimy = new THREE.MeshStandardMaterial({ map: grimyPlasterTexture(), roughness: 0.96, side: THREE.DoubleSide });
  // Windows-only emissive whisper (the M116 pattern, but with a dedicated
  // glow layer instead of the whole texture — walls never wash).
  const facade = new THREE.MeshStandardMaterial({
    map: facadeTexture(),
    roughness: 0.92,
    emissive: new THREE.Color(0xffffff),
    emissiveMap: facadeGlowTexture(),
    // 0.55 read as lamps burning at NOON (measured from the block side) —
    // 0.35 keeps the whisper: invisible in sun, faintly warm in shade.
    emissiveIntensity: 0.35,
    side: THREE.DoubleSide,
  });
  const street = new THREE.MeshStandardMaterial({
    map: streetTexture(),
    roughness: 0.94,
    side: THREE.DoubleSide,
  });
  const mezz = new THREE.MeshStandardMaterial({
    map: mezzTexture(),
    roughness: 0.93,
    side: THREE.DoubleSide,
  });
  const base = new THREE.MeshStandardMaterial({
    map: baseTexture(),
    roughness: 0.95,
    side: THREE.DoubleSide,
  });
  // Setts tiled by REAL size (~0.19 u each): chStrip gives u = along/uvStep(3)
  // and v = 0..1 across the 24 u ribbon, so 8 repeats across match 1 along.
  const paveMap = paveTexture();
  paveMap.repeat.set(1, 8);
  // DoubleSide like every other chStrip material here: a strip's winding puts
  // its normal down as often as up, and a FrontSide sheet is simply invisible
  // from above when it lands the wrong way (measured — the first paving pass
  // rendered nothing at all).
  const pave = new THREE.MeshStandardMaterial({ map: paveMap, roughness: 0.97, side: THREE.DoubleSide });
  const towers = [0xdf9d8a, 0xf2e3bc, 0xeab35c, 0xe07a52, 0xeacb70].map((c) => {
    const m = new THREE.MeshStandardMaterial({ map: plasterTexture(), roughness: 0.95 });
    m.color = new THREE.Color(c);
    return m;
  });
  const cloths = CLOTHS.map((c) => {
    const m = mat(c, 0.95);
    m.side = THREE.DoubleSide;
    return m;
  });
  const veil = (map: THREE.CanvasTexture, rough: number): THREE.MeshStandardMaterial =>
    new THREE.MeshStandardMaterial({ map, transparent: true, depthWrite: false, roughness: rough, side: THREE.DoubleSide });
  // The board runs are BoxGeometry (UV 0..1 per face), so tiling has to come
  // from the map: 3 repeats along the ~9.6 u run puts the painted 8-board
  // course at a real ~0.4 u per board (see scaffoldPlankTexture). `planks` is
  // used by the deck runs and nothing else, so the repeat is safely scoped.
  const plankMap = scaffoldPlankTexture();
  plankMap.repeat.set(1, 3);
  return {
    tile,
    plaster,
    grimy,
    facade,
    street,
    mezz,
    base,
    pave,
    terra: mat(NAP.terra, 0.88),
    cotto: mat(0x8f4526, 0.94), // darker roof-cap cotto (the zone's cap language)
    iron: mat(NAP.iron, 0.62),
    steel: mat(NAP.steel, 0.58),
    timber: mat(NAP.timber, 0.92),
    rubble: mat(NAP.rubble, 0.98),
    tank: mat(0x7a9ab0, 0.7),
    shutter: mat(NAP.shutter, 0.9),
    trim: (() => {
      const t = new THREE.MeshStandardMaterial({ map: plaster.map, roughness: 0.96 });
      t.color = new THREE.Color(0xbdb199);
      return t;
    })(),
    towers,
    cloths,
    planks: new THREE.MeshStandardMaterial({ map: plankMap, roughness: 0.88 }),
    // Worn paint, not a light bar: a MAPPED decal (the M117f runway lesson).
    hazard: new THREE.MeshBasicMaterial({ map: hazardTexture(), transparent: true, opacity: 0.9, depthWrite: false }),
    sheen: new THREE.MeshBasicMaterial({
      map: sheenTexture(),
      transparent: true,
      opacity: 0.3, // × the texture's own 0.14..0.44 alpha = a whisper
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
    tarp: new THREE.MeshStandardMaterial({ map: tarpTexture(), roughness: 0.82, side: THREE.DoubleSide }),
    hoard: new THREE.MeshStandardMaterial({ map: hoardingTexture(), roughness: 0.9, side: THREE.DoubleSide }),
    sand: mat(0xc0a878, 0.98),
    concrete: mat(0x9a9a94, 0.95),
    turf: new THREE.MeshStandardMaterial({ map: turfTexture(), roughness: 0.96 }),
    chain: veil(chainTexture(), 0.68),
    goalNet: veil(goalNetTexture(), 0.85),
    white: mat(0xe4e8ea, 0.6),
    lamp: mat(0xfff0c8, 0.4, 0xffd08a, 1.5),
    ball: mat(0xf4f6f8, 0.72),
  };
}

// ---- The renovation scaffold launcher -------------------------------------

/** THE WORKS (M117f owner round: "the ramps still look too much like ramps
 *  and not enough like roadwork"). A live renovation site whose plank runs
 *  happen to make a kicker — irregular boards on a lopsided tube frame, a
 *  sagging tarp, and a working yard of mixer/spoil/sandbags/kerbstones/
 *  hoarding around it. The go-fast cue is worn hazard tape at the lip plus
 *  a faint wet sheen; the lit centre runway is gone.
 *
 *  LANE DISCIPLINE (measured on this course): the ramp occupies road
 *  lateral 4..10 and the guardrail sits at 18, so `b` (across, positive =
 *  toward the wall) splits into two bands — 4.5..9.5 is the dead outer
 *  lane no racing line uses (LOW mess under ~0.5 u nearest the ramp, the
 *  bulky machinery at 7.4+ = 4.4 u clear of the ramp edge), and 12.5+ is
 *  beyond the rail, where only the hoarding line and the spoil heap live
 *  (everything else put there simply disappeared — see the header). */
function buildScaffoldRamp(track: Track, sc: PlacedShortcut, g: THREE.Group, m: Mats): void {
  const k = sc.kicker as KickerFrame;
  const slopeLen = Math.hypot(k.length, k.height);
  const pitch = -Math.atan2(k.height, k.length);
  const fx = Math.sin(k.dir);
  const fz = Math.cos(k.dir);
  const rx = Math.cos(k.dir); // launch-frame right (§6.1)
  const rz = -Math.sin(k.dir);
  const outer = sc.door.side >= 0 ? 1 : -1; // the course-wall side of the ramp
  const hw = k.halfWidth;
  const cosP = k.length / slopeLen; // ground run per unit of slope
  const sinP = k.height / slopeLen;
  /** Ramp frame → world. `a` = along the ground from the foot, `b` = across
   *  (positive is always the WALL/verge side, whichever way the door faces). */
  const P = (a: number, b: number): [number, number] => [
    k.x + fx * a + rx * outer * b,
    k.z + fz * a + rz * outer * b,
  ];
  /** A point `s` up the SLOPE (0 = foot, slopeLen = lip), offset `b` across. */
  const S = (s: number, b: number): [number, number, number] => {
    const [x, z] = P(s * cosP, b);
    return [x, k.y + s * sinP, z];
  };
  const groundAt = (a: number, b: number): number => {
    const [x, z] = P(a, b);
    return track.heightAt(x, z);
  };
  const yawAcross = k.dir + Math.PI / 2;

  // ---- The running surface: THREE uneven plank runs (welded = 1 draw call).
  // One starts short of the foot (you see the bearer under it), one juts a
  // board's length past the lip, and they sit at slightly different heights.
  // Physics never reads these meshes, so the unevenness is free character.
  const runs: BoxPart[] = [];
  for (const [b, w, s0, s1, dy] of [
    [-2.0, 2.3, 0, slopeLen + 0.42, 0.012],
    [0, 2.3, 0.55, slopeLen, 0],
    [2.0, 2.4, 0, slopeLen - 0.28, -0.03],
  ] as const) {
    const len = s1 - s0;
    const [x, y, z] = S((s0 + s1) / 2, b);
    runs.push({ w, h: 0.26, d: len, x, y: y + dy - 0.13 * Math.cos(pitch), z, yaw: k.dir, pitch });
  }
  g.add(weld(runs, m.planks));

  // Timber toe boards — one full, one broken into two lengths with a gap.
  const timber: BoxPart[] = [];
  for (const [b, s0, s1] of [
    [-(hw - 0.12), 0.1, slopeLen * 0.98],
    [hw - 0.12, 0.1, slopeLen * 0.44],
    [hw - 0.12, slopeLen * 0.6, slopeLen * 0.96],
  ] as const) {
    const [x, y, z] = S((s0 + s1) / 2, b);
    timber.push({ w: 0.22, h: 0.3, d: s1 - s0, x, y: y + 0.1, z, yaw: k.dir, pitch });
  }

  // ---- The go-fast cue, incidental (the M117f runway lesson) --------------
  // Incline-decal rotation is −π/2 − atan(slope) (= −π/2 + pitch); the
  // negated form tips the decal INTO the ramp (measured on the Lagos jetty).
  const decal = (
    wide: number,
    along: number,
    s: number,
    b: number,
    material: THREE.Material,
    lift: number,
    spin = 0,
  ): void => {
    const [x, y, z] = S(s, b);
    const d = new THREE.Mesh(new THREE.PlaneGeometry(wide, along), material);
    d.position.set(x, y + lift, z);
    d.rotation.order = 'YXZ';
    d.rotation.y = k.dir;
    d.rotation.x = -Math.PI / 2 + pitch;
    d.rotation.z = spin;
    g.add(d);
  };
  // Damp boards: a wide streaky sheen, not a lane. Tiled by real size so the
  // smears stay board-sized however long the run is.
  if (m.sheen.map) m.sheen.map.repeat.set(1.5, slopeLen / 3.4);
  decal(5.4, slopeLen * 0.86, slopeLen * 0.5, -0.15, m.sheen, 0.15);
  // Worn hazard tape: a full band right at the lip + a scuffed remnant
  // further down that has mostly been driven off.
  decal(5.7, 1.45, slopeLen - 0.95, 0, m.hazard, 0.17);
  decal(2.3, 0.75, slopeLen * 0.38, -1.1, m.hazard, 0.17, 0.06);

  // ---- The frame: deliberately lopsided --------------------------------
  const steel: BoxPart[] = [];
  const post = (a: number, b: number, top: number, roll = 0): void => {
    const [x, z] = P(a, b);
    const gy = groundAt(a, b);
    const h = Math.max(0.8, top - gy);
    steel.push({ w: 0.13, h, d: 0.13, x, y: gy + h / 2, z, yaw: k.dir, roll });
  };
  // Road side: three posts, the middle one knocked out of plumb.
  post(k.length * 0.1, -(hw + 0.28), k.y + k.height * 0.28 + 0.55);
  post(k.length * 0.52, -(hw + 0.28), k.y + k.height * 0.62 + 0.55, 0.07);
  post(k.length * 0.9, -(hw + 0.28), k.y + k.height * 1.0 + 0.55);
  // Wall side: a post is MISSING mid-span — a long diagonal brace does its
  // job instead, which is what actually makes the frame read as improvised.
  post(k.length * 0.06, hw + 0.28, k.y + k.height * 0.24 + 0.4);
  post(k.length * 0.72, hw + 0.28, k.y + k.height * 0.86 + 0.7);
  post(k.length * 0.94, hw + 0.28, k.y + k.height * 1.0 + 0.42);
  {
    const [ax, az] = P(k.length * 0.14, hw + 0.28);
    const [bx2, bz2] = P(k.length * 0.7, hw + 0.28);
    const ay = groundAt(k.length * 0.14, hw + 0.28) + 0.1;
    const by2 = k.y + k.height * 0.86 + 0.6;
    const run = Math.hypot(bx2 - ax, bz2 - az);
    steel.push({
      w: 0.11,
      h: 0.11,
      d: Math.hypot(run, by2 - ay),
      x: (ax + bx2) / 2,
      y: (ay + by2) / 2,
      z: (az + bz2) / 2,
      yaw: k.dir,
      pitch: -Math.atan2(by2 - ay, run),
    });
  }
  // Ledger rails: full length on the road side, only the top half on the
  // wall side (that run is still being built).
  for (const [b, s0, s1, lift] of [
    [-(hw + 0.28), 0, slopeLen * 1.02, 0.62],
    [hw + 0.28, slopeLen * 0.42, slopeLen * 1.0, 0.7],
  ] as const) {
    const [x, y, z] = S((s0 + s1) / 2, b);
    steel.push({ w: 0.1, h: 0.1, d: s1 - s0, x, y: y + lift, z, yaw: k.dir, pitch });
  }
  {
    const [x, z] = P(k.length * 0.9, 0);
    steel.push({ w: (hw + 0.28) * 2, h: 0.1, d: 0.1, x, y: k.y + k.height * 0.45, z, yaw: k.dir });
  }
  // Two standards past the lip at DIFFERENT heights (no crossbar — the
  // flight path goes through that gap and nothing solid may span it).
  post(k.length + 0.7, -(hw + 0.3), k.y + k.height + 2.5);
  post(k.length + 0.7, hw + 0.3, k.y + k.height + 1.7);

  // ---- Tarpaulin sagging over the upper wall-side frame -----------------
  {
    const pos: number[] = [];
    const uv: number[] = [];
    const idx: number[] = [];
    const N = 8;
    for (let i = 0; i <= N; i++) {
      const t = i / N;
      const s = slopeLen * (0.3 + t * 0.68);
      const sag = Math.sin(t * Math.PI) * 0.38;
      const [tx, ty, tz] = S(s, hw + 0.3);
      const [bx2, by2, bz2] = S(s, hw + 1.15);
      pos.push(tx, ty + 0.7 - sag * 0.35, tz, bx2, by2 - 1.15 - sag, bz2);
      uv.push(t * 3.2, 0, t * 3.2, 1);
      if (i > 0) {
        const kk = i * 2;
        idx.push(kk - 2, kk - 1, kk, kk - 1, kk + 1, kk);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    geo.setIndex(idx);
    geo.computeVertexNormals();
    const tarp = new THREE.Mesh(geo, m.tarp);
    tarp.matrixAutoUpdate = false;
    g.add(tarp);
  }

  // Orange debris netting on the LOWER wall side only (the tarp covers the
  // upper half — the split is what stops the elevation reading symmetric).
  {
    const netMat = new THREE.MeshStandardMaterial({
      map: nettingTexture(),
      transparent: true,
      depthWrite: false,
      roughness: 0.9,
      side: THREE.DoubleSide,
    });
    const netH = k.height + 1.2;
    const s0 = 0.05;
    const s1 = slopeLen * 0.56;
    const [x, , z] = S((s0 + s1) / 2, hw + 0.36);
    const gy = groundAt(((s0 + s1) / 2) * cosP, hw + 0.36);
    const net = new THREE.Mesh(new THREE.PlaneGeometry(s1 - s0, netH), netMat);
    net.position.set(x, gy + netH / 2, z);
    net.rotation.y = yawAcross;
    g.add(net);
  }

  // LAVORI IN CORSO board zip-tied to the ROAD-side frame at mid-slope.
  {
    const boardMat = new THREE.MeshStandardMaterial({ map: lavoriTexture(), roughness: 0.85, side: THREE.DoubleSide });
    const [x, y, z] = S(slopeLen * 0.45, -(hw + 0.42));
    const board = new THREE.Mesh(new THREE.PlaneGeometry(2.2, 1.55), boardMat);
    board.position.set(x, y + 0.9, z);
    board.rotation.order = 'YXZ';
    // FACE THE APPROACH (the M116b lesson): a plane's normal is local +Z, so
    // pointing it back across the run (−outer·right) needs dir − outer·π/2.
    // The other sign renders the sign mirrored to the driver — measured.
    board.rotation.y = k.dir - outer * (Math.PI / 2);
    board.rotation.x = -0.06;
    board.rotation.z = 0.05;
    g.add(board);
  }

  // ---- The yard ---------------------------------------------------------
  const concrete: BoxPart[] = [];
  const sandbags: BoxPart[] = [];
  const hoard: BoxPart[] = [];

  // LOW mess in the dead outer lane: mortar spill, offcuts, kerbstones.
  for (const [a, b, w, d, yaw] of [
    [-1.6, 5.2, 2.1, 1.5, 0.3],
    [0.9, 6.5, 1.6, 1.2, -0.5],
  ] as const) {
    concrete.push({ w, h: 0.09, d, x: P(a, b)[0], y: groundAt(a, b) + 0.045, z: P(a, b)[1], yaw: k.dir + yaw });
  }
  for (let i = 0; i < 5; i++) {
    const a = -3.4 + art(i * 7 + 3) * 8.5;
    const b = 4.7 + art(i * 11 + 5) * 4.4;
    const [x, z] = P(a, b);
    timber.push({
      w: 0.24 + art(i * 13) * 0.16,
      h: 0.11,
      d: 1.6 + art(i * 17) * 1.9,
      x,
      y: groundAt(a, b) + 0.06 + art(i * 19) * 0.06,
      z,
      yaw: k.dir + (art(i * 23) - 0.5) * 2.4,
    });
  }
  for (let i = 0; i < 4; i++) {
    // A kerbstone stack, each course kicked a little off the last.
    const a = 3.0 + (art(i * 3) - 0.5) * 0.3;
    const b = 7.4 + (art(i * 5) - 0.5) * 0.3;
    const [x, z] = P(a, b);
    concrete.push({
      w: 0.52,
      h: 0.21,
      d: 1.65,
      x,
      y: groundAt(a, b) + 0.11 + i * 0.21,
      z,
      yaw: k.dir + (art(i * 29) - 0.5) * 0.28,
    });
  }
  {
    // Spilled sand at the foot of the run.
    const a = -3.6;
    const b = 6.1;
    const [x, z] = P(a, b);
    const heap = new THREE.Mesh(new THREE.ConeGeometry(1.35, 0.8, 8), m.sand);
    heap.position.set(x, groundAt(a, b) + 0.4, z);
    heap.rotation.y = 0.4;
    g.add(heap);
  }
  // The plank pile that was always here, now leaning on the frame.
  {
    const a = -2.2;
    const b = hw + 0.9;
    const [x, z] = P(a, b);
    timber.push({ w: 1.1, h: 0.34, d: 2.6, x, y: groundAt(a, b) + 0.17, z, yaw: k.dir + 0.16 });
    const tub = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.34, 0.42, 8), m.tank);
    const [tx, tz] = P(a, b + 1.1);
    tub.position.set(tx, groundAt(a, b + 1.1) + 0.21, tz);
    g.add(tub);
  }

  // BEYOND THE RAIL: the tall silhouettes that make the whole thing read as
  // a works compound from up the road (nothing here is reachable).
  for (const [i, a] of [-6.6, -2.7, 1.2].entries()) {
    const b = 12.8;
    const [x, z] = P(a, b);
    const gy = groundAt(a, b);
    hoard.push({
      w: 0.08,
      h: 2.2,
      d: 3.5,
      x,
      y: gy + 1.15,
      z,
      yaw: k.dir + (art(i * 31) - 0.5) * 0.18,
      roll: (art(i * 37) - 0.5) * 0.07,
    });
    for (const off of [-1.65, 1.65]) {
      const [px, pz] = P(a + off, b + 0.16);
      steel.push({ w: 0.12, h: 2.5, d: 0.12, x: px, y: groundAt(a + off, b + 0.16) + 1.25, z: pz, yaw: k.dir });
    }
  }
  {
    // Cement mixer: tilted drum on a wheeled stand. Parked in the dead lane
    // (road lateral ~15.6) rather than beyond the rail — measured: over the
    // parapet the whole compound vanished from the driver's approach, and
    // the yard has to be part of what funnels you onto the boards.
    const a = 1.2;
    const b = 8.8;
    const gy = groundAt(a, b);
    const [x, z] = P(a, b);
    const drum = new THREE.Mesh(new THREE.CylinderGeometry(0.66, 0.44, 1.15, 10), m.tank);
    drum.position.set(x, gy + 1.32, z);
    drum.rotation.order = 'YXZ';
    drum.rotation.y = k.dir;
    drum.rotation.x = 0.95;
    g.add(drum);
    for (const off of [-0.52, 0.52]) {
      const [lx, lz] = P(a + off, b);
      steel.push({ w: 0.11, h: 1.0, d: 0.11, x: lx, y: gy + 0.5, z: lz, yaw: k.dir, roll: off > 0 ? 0.2 : -0.2 });
      const [wx, wz] = P(a + off, b - 0.5);
      steel.push({ w: 0.1, h: 0.44, d: 0.44, x: wx, y: gy + 0.22, z: wz, yaw: k.dir });
    }
    steel.push({ w: 1.2, h: 0.14, d: 0.5, x, y: gy + 0.95, z, yaw: k.dir });
    steel.push({ w: 0.42, h: 0.4, d: 0.5, x: P(a - 0.9, b)[0], y: gy + 0.75, z: P(a - 0.9, b)[1], yaw: k.dir });
  }
  {
    // Spoil heap of excavated tufa, tipped just behind the hoarding. Kept
    // SMALL (r 1.9): at 2.7 it read as a boulder dumped in a green field,
    // not as excavation from a street trench (measured from outside).
    const a = -7.4;
    const b = 13.9;
    const [x, z] = P(a, b);
    const gy = groundAt(a, b);
    const heap = new THREE.Mesh(new THREE.ConeGeometry(1.9, 1.5, 9), m.rubble);
    heap.position.set(x, gy + 0.72, z);
    heap.rotation.y = 0.7;
    g.add(heap);
    for (let i = 0; i < 3; i++) {
      const [rx2, rz2] = P(a + (art(i * 41) - 0.5) * 4, b + (art(i * 43) - 0.5) * 2.4);
      const rock = new THREE.Mesh(new THREE.IcosahedronGeometry(0.22 + art(i * 47) * 0.18, 0), m.rubble);
      rock.position.set(rx2, gy + 0.16, rz2);
      rock.rotation.set(art(i * 53) * 3, art(i * 59) * 3, art(i * 61) * 3);
      g.add(rock);
    }
  }
  for (let i = 0; i < 7; i++) {
    // Sandbag stack, slumped, on the wall side of the dead lane.
    const row = Math.floor(i / 3);
    const col = i % 3;
    // MEASURED: anything past a ≈ +2 hides behind the ramp on the driver's
    // approach, so the whole yard clusters at the foot where it is seen.
    const a = -5.2 + (col - 1) * 0.78 + row * 0.2;
    const b = 8.4 + row * 0.1;
    const [x, z] = P(a, b);
    sandbags.push({
      w: 0.72,
      h: 0.3,
      d: 0.46,
      x,
      y: groundAt(a, b) + 0.16 + row * 0.29,
      z,
      yaw: k.dir + (art(i * 67) - 0.5) * 0.5,
      roll: (art(i * 71) - 0.5) * 0.16,
    });
  }
  for (let i = 0; i < 6; i++) {
    // Scaffold-tube bundle stacked 3 / 2 / 1.
    const row = i < 3 ? 0 : i < 5 ? 1 : 2;
    const col = i < 3 ? i : i < 5 ? i - 3 : 0;
    const a = -1.4;
    const b = 7.6 + col * 0.26 + row * 0.13;
    const [x, z] = P(a, b);
    steel.push({ w: 0.22, h: 0.22, d: 4.6, x, y: groundAt(a, b) + 0.12 + row * 0.23, z, yaw: k.dir + 0.05 });
  }
  {
    // Wheelbarrow parked beside the boards.
    const a = -0.6;
    const b = 5.4;
    const [x, z] = P(a, b);
    const gy = groundAt(a, b);
    timber.push({ w: 0.86, h: 0.42, d: 1.15, x, y: gy + 0.52, z, yaw: k.dir + 0.5, pitch: 0.34 });
    for (const off of [-0.3, 0.3]) {
      const [hx, hz] = P(a - 0.9 + off * 0.2, b + off);
      steel.push({ w: 0.08, h: 0.08, d: 1.5, x: hx, y: gy + 0.62, z: hz, yaw: k.dir + 0.5, pitch: 0.3 });
    }
    const [wx, wz] = P(a + 0.85, b);
    steel.push({ w: 0.1, h: 0.5, d: 0.5, x: wx, y: gy + 0.25, z: wz, yaw: k.dir + 0.5 });
  }

  g.add(weld(steel, m.steel));
  g.add(weld(timber, m.timber));
  g.add(weld(concrete, m.concrete));
  g.add(weld(sandbags, m.sand));
  g.add(weld(hoard, m.hoard));

  // Two amber site lamps: one on the tall road-side standard, one on the
  // hoarding — the compound's only lights at night.
  for (const [a, b, up] of [
    [k.length + 0.7, -(hw + 0.3), k.height + 2.55],
    [-2.7, 12.9, 2.6],
  ] as const) {
    const [x, z] = P(a, b);
    const lamp = new THREE.Mesh(new THREE.SphereGeometry(0.2, 8, 6), m.lamp);
    lamp.position.set(x, groundAt(a, b) + up, z);
    g.add(lamp);
  }
}

// ---- The rooftop run ------------------------------------------------------

/** The deck as a BLOCK OF HOUSES: barrel-tile terrace floor, stucco
 *  parapets with a broken crest at the exit, painted pastel facades
 *  dropping to the ground (the houses are the support — no columns), and
 *  the entry gable you land over. */
function buildRooftopRun(track: Track, sc: PlacedShortcut, g: THREE.Group, m: Mats): void {
  const ch = sc.channel;
  const laneHalf = ch.halfWidth + 0.35; // the stucco parapet line (visual rail)
  const roofHalf = laneHalf + ROOF_OVERHANG; // the building's outer edge
  const nEnd = nodeAt(ch, ch.length - tuning.shortcuts.stubLen - 1);
  const fadeFrom = nEnd - Math.ceil(CREST_FADE / ch.step);
  /** Parapet crest: full height most of the run, breaking down (jagged,
   *  shrinking) over the last CREST_FADE units — the collapsed roof. */
  const crest = (i: number): number => {
    const fade = Math.min(1, Math.max(0, (nEnd - i) / Math.max(1, nEnd - fadeFrom)));
    if (fade >= 1) return PARA_H;
    return 0.14 + (PARA_H - 0.14) * fade * (0.65 + art(i * 3 + 7) * 0.35);
  };

  // Terrace floor across the FULL roof width (the drive lane + the outer
  // band the chimneys stand on).
  g.add(
    chStrip(ch.nodes, ch.step, 0, nEnd, (nd) => [
      new THREE.Vector3(nd.x - nd.rightX * roofHalf, nd.y + 0.03, nd.z - nd.rightZ * roofHalf),
      new THREE.Vector3(nd.x + nd.rightX * roofHalf, nd.y + 0.03, nd.z + nd.rightZ * roofHalf),
    ], m.tile, 6),
  );
  // Landing apron: a slightly wider flare over the first nodes. Kept to a
  // +0.2 flare — a wider one reads as a thin plate poking past the gable
  // roofline from the road (measured). NO under-lip glow here: the batch-1
  // glow plane hung exactly where the gable house faces now stand and
  // washed them pale (measured) — the parapet lantern strips carry the
  // night cue instead.
  g.add(
    chStrip(ch.nodes, ch.step, 0, 2, (nd) => [
      new THREE.Vector3(nd.x - nd.rightX * (roofHalf + 0.2), nd.y + 0.055, nd.z - nd.rightZ * (roofHalf + 0.2)),
      new THREE.Vector3(nd.x + nd.rightX * (roofHalf + 0.2), nd.y + 0.055, nd.z + nd.rightZ * (roofHalf + 0.2)),
    ], m.tile, 6),
  );
  const n0 = ch.nodes[0];
  // The entry gable — the first houses, whose roof is the landing. Flying
  // in, you pass OVER them; from the road they ground the block's leading
  // end. TWO offset pastel house-ends, not one slab (a single grimy box
  // read as a blank warehouse from the road — measured), each with its own
  // cotto lid and a stepped roofline.
  {
    const gy = track.heightAt(n0.x, n0.z);
    for (const [hs, drop, tIdx] of [
      [-1, 0.16, 1], // cream + terracotta — same-hue neighbours melted into
      [1, 0.62, 3], // one slab from the road (measured)
    ] as const) {
      const hw2 = roofHalf * 0.5;
      const hx = n0.x + Math.sin(n0.dir) * 0.55 + n0.rightX * hs * hw2;
      const hz = n0.z + Math.cos(n0.dir) * 0.55 + n0.rightZ * hs * hw2;
      const h = Math.max(1.2, n0.y - drop - (gy - 0.4));
      g.add(box(roofHalf + 0.02, h, 1.0, m.towers[tIdx], hx, gy - 0.4 + h / 2, hz, n0.dir));
      g.add(box(roofHalf + 0.3, 0.14, 1.25, m.cotto, hx, gy - 0.4 + h + 0.07, hz, n0.dir));
    }
  }

  // Stucco parapets: one vertical band + a terracotta coping cap per side,
  // both following the broken crest.
  for (const s of [-1, 1] as const) {
    g.add(
      chStrip(ch.nodes, ch.step, 0, nEnd, (nd, i) => {
        const x = nd.x + nd.rightX * s * laneHalf;
        const z = nd.z + nd.rightZ * s * laneHalf;
        return [new THREE.Vector3(x, nd.y + 0.02, z), new THREE.Vector3(x, nd.y + crest(i), z)];
      }, m.plaster, 4),
    );
    g.add(
      chStrip(ch.nodes, ch.step, 0, nEnd, (nd, i) => {
        const y = nd.y + crest(i);
        return [
          new THREE.Vector3(nd.x + nd.rightX * s * (laneHalf - 0.22), y, nd.z + nd.rightZ * s * (laneHalf - 0.22)),
          new THREE.Vector3(nd.x + nd.rightX * s * (laneHalf + 0.22), y, nd.z + nd.rightZ * s * (laneHalf + 0.22)),
        ];
      }, m.terra, 4),
    );
  }
  // The warm lantern line at the parapet (night readability, subtle) — held
  // short of the broken-crest zone so it never floats over rubble.
  glowStrips(g, ch, 2, fadeFrom, laneHalf - 0.06, PARA_H - 0.14, NAP.accent);

  // THE FACADES — FOUR disjoint bands per side, only the lowest of the middle
  // pair variable (the stretch gotcha above): a constant WINDOW band hung from
  // the roof edge, a near-constant MEZZANINE hung under it, a constant STREET
  // band standing on the ground, and a BASE WALL between mezzanine and street
  // that absorbs every bit of the deck's height change. The mezzanine is the
  // M117g nit fix: the middle band used to be one featureless grimy filler and
  // it was the loudest thing on the block from the street.
  for (const s of [-1, 1] as const) {
    const edge = (nd: { x: number; z: number; rightX: number; rightZ: number }): [number, number] => [
      nd.x + nd.rightX * s * roofHalf,
      nd.z + nd.rightZ * s * roofHalf,
    ];
    // Over-bay test per node, computed ONCE and shared by the three strips
    // (hint-less project = full scan, the M99 rule — a handful of nodes).
    const dry: boolean[] = [];
    for (let i = 0; i <= nEnd; i++) {
      const [x, z] = edge(ch.nodes[i]);
      dry.push(!overBay(track, sc, x, z));
    }
    /** Solve the three bands at one node. MEASURED clearance profile on this
     *  course: median 4.7, p75 6.1, max 6.7, and 82% of the skirt is over
     *  2.4 — so a real ground floor fits nearly everywhere, which is the whole
     *  point (the block has to survive being looked at from the street). */
    const bands = (nd: (typeof ch.nodes)[number], i: number) => {
      const [x, z] = edge(nd);
      const gy = track.heightAt(x, z);
      const top = nd.y - 0.06;
      // The ground floor only stands on LAND: mid-ride the block interior is
      // partly the BAY (the lungomare's sheets reach inside the road loop),
      // and doors in the sea are worse than no doors — there the old rule
      // holds and the grimy base reads as the harbour mole it always did.
      const hasStreet = dry[i] && nd.y - gy >= STREET_H + 0.35;
      const floor = hasStreet ? gy - 0.3 + STREET_H + 0.02 : gy + 0.55;
      const winBot = Math.max(floor, top - FACADE_H);
      // Squashed windows read as a rendering bug, not a low house — below
      // this the upper storey is dropped and the ground floor carries it.
      const hasWin = top - winBot >= 1.9;
      // What is left between the ground floor and the window band is the
      // MEZZANINE's slice plus the base wall's remainder. Same rule as the
      // window band: the storey collapses whole rather than squashing.
      const fillBot = hasStreet ? gy - 0.3 + STREET_H : gy - 0.45;
      const fillTop = hasWin ? winBot + 0.02 : top;
      const gapH = fillTop - fillBot;
      const hasMezz = gapH >= MEZZ_MIN + 0.04;
      const mezzBot = hasMezz ? fillTop - Math.min(MEZZ_MAX, gapH - 0.04) : fillTop;
      return { x, z, gy, top, hasStreet, hasWin, winBot, floor, fillBot, fillTop, hasMezz, mezzBot };
    };
    const flat = (x: number, y: number, z: number): [THREE.Vector3, THREE.Vector3] => [
      new THREE.Vector3(x, y, z),
      new THREE.Vector3(x, y, z),
    ];
    g.add(
      chStrip(ch.nodes, ch.step, 0, nEnd, (nd, i) => {
        const b = bands(nd, i);
        if (!b.hasWin) return flat(b.x, b.top, b.z);
        return [new THREE.Vector3(b.x, b.winBot, b.z), new THREE.Vector3(b.x, b.top, b.z)];
      }, m.facade, 26),
    );
    g.add(
      chStrip(ch.nodes, ch.step, 0, nEnd, (nd, i) => {
        const b = bands(nd, i);
        if (!b.hasStreet) return flat(b.x, b.gy, b.z);
        return [
          new THREE.Vector3(b.x, b.gy - 0.3, b.z),
          new THREE.Vector3(b.x, b.gy - 0.3 + STREET_H, b.z),
        ];
      }, m.street, 26),
    );
    g.add(
      chStrip(ch.nodes, ch.step, 0, nEnd, (nd, i) => {
        const b = bands(nd, i);
        if (!b.hasMezz) return flat(b.x, b.fillTop, b.z);
        return [new THREE.Vector3(b.x, b.mezzBot, b.z), new THREE.Vector3(b.x, b.fillTop, b.z)];
      }, m.mezz, 26),
    );
    g.add(
      chStrip(ch.nodes, ch.step, 0, nEnd, (nd, i) => {
        const b = bands(nd, i);
        return [
          new THREE.Vector3(b.x, b.fillBot, b.z),
          new THREE.Vector3(b.x, Math.max(b.fillBot, b.mezzBot), b.z),
        ];
      }, m.base, 26),
    );
  }
}

// ---- Roof life ------------------------------------------------------------

/** Chimney pots, water tanks, TV aerials and stair bulkheads scattered on
 *  the outer roof band — everything OUTSIDE the drivable width (centres at
 *  |lateral| ≥ deckHalf + 0.85; the rule floor is +0.6). */
function buildRoofLife(sc: PlacedShortcut, g: THREE.Group, m: Mats): void {
  const ch = sc.channel;
  const nEnd = nodeAt(ch, ch.length - tuning.shortcuts.stubLen - 1);
  const stride = Math.max(2, Math.round(20 / ch.step));
  for (let i = stride; i < nEnd - 6; i += stride) {
    const nd = ch.nodes[i];
    const s = art(i * 3 + 11) < 0.5 ? -1 : 1;
    const off = 3.95 + art(i * 5 + 7) * 0.55;
    const px = nd.x + nd.rightX * s * off;
    const pz = nd.z + nd.rightZ * s * off;
    const py = nd.y + 0.03;
    const kind = Math.floor(art(i * 7 + 3) * 4);
    if (kind === 0) {
      // Chimney: stucco base + terracotta pot(s).
      g.add(box(0.55, 0.9, 0.55, m.grimy, px, py + 0.45, pz, nd.dir));
      const pot = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.17, 0.5, 6), m.terra);
      pot.position.set(px, py + 1.12, pz);
      g.add(pot);
      if (art(i * 11 + 5) < 0.45) {
        const pot2 = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.15, 0.42, 6), m.terra);
        pot2.position.set(px + nd.rightX * s * 0.3, py + 1.08, pz + nd.rightZ * s * 0.3);
        g.add(pot2);
      }
    } else if (kind === 1) {
      // Rooftop water tank (the Naples terrace fixture).
      const tank = new THREE.Mesh(new THREE.CylinderGeometry(0.48, 0.48, 0.72, 8), m.tank);
      tank.position.set(px, py + 0.4, pz);
      g.add(tank);
    } else if (kind === 2) {
      // TV aerial: pole + two cross arms, slightly askew like the real ones.
      g.add(box(0.06, 2.3, 0.06, m.iron, px, py + 1.15, pz, nd.dir));
      g.add(box(1.0, 0.045, 0.045, m.iron, px, py + 2.0, pz, nd.dir + (art(i * 13) - 0.5) * 0.7));
      g.add(box(0.75, 0.045, 0.045, m.iron, px, py + 1.7, pz, nd.dir + (art(i * 17) - 0.5) * 0.7));
    } else {
      // Stair bulkhead: the little terrace-access hut, pastel + cotto lid.
      const tint = m.towers[Math.floor(art(i * 19 + 1) * m.towers.length) % m.towers.length];
      g.add(box(1.25, 1.05, 1.05, tint, px, py + 0.52, pz, nd.dir));
      g.add(box(1.45, 0.1, 1.25, m.cotto, px, py + 1.1, pz, nd.dir));
    }
  }
}

// ---- Laundry spans --------------------------------------------------------

/** Lines strung between taller palazzo towers flanking the lane. The line
 *  hangs at deck+8.5 so the LOWEST cloth stays ≥ 6.2 over the floor (chase
 *  cam clearance — the measured M116 rule); the towers that height demands
 *  are what make the spans read as buildings, not poles.
 *
 *  M117g NIT (QA: deck-skirt-street, block-courtyard — "chimney-like pillars
 *  stand alone with no house under them"): at 1.7 × 1.7 and ~15 u tall these
 *  towers were sticks. Nothing about their PLACEMENT was wrong; they simply had
 *  no house in them. They are now 2.6 across × 3.7 along with real openings —
 *  shuttered windows floor by floor, a doorway at the foot, a cotto cornice at
 *  deck level (the roof of the storeys the ride passes) and the terrace lid on
 *  top. LANE DISCIPLINE IS UNCHANGED: the inner face still stands at 3.75, i.e.
 *  outside deckHalf + 0.6, so the widening went OUTWARD only. */
function buildLaundrySpans(track: Track, sc: PlacedShortcut, g: THREE.Group, m: Mats): void {
  const ch = sc.channel;
  const nEnd = nodeAt(ch, ch.length - tuning.shortcuts.stubLen - 1);
  const lineMat = mat(0xd8d2c2, 1);
  const spans = [60, 160, 250];
  const TW = 2.6; // across the ride
  const TD = 3.7; // along the ride
  // Openings for every tower, welded ONCE per material at the end — a palazzo
  // costs three draw calls for the whole run, not three per floor.
  const glass: BoxPart[] = [];
  const shutters: BoxPart[] = [];
  const stone: BoxPart[] = [];
  for (const [si, d] of spans.entries()) {
    const i = nodeAt(ch, d);
    if (i >= nEnd - 8) continue;
    const nd = ch.nodes[i];
    const off = 5.05; // tower inner face at 3.75 = the prop rule floor
    const lineY = nd.y + 8.5;
    const fx = Math.sin(nd.dir);
    const fz = Math.cos(nd.dir);
    for (const s of [-1, 1] as const) {
      const tx = nd.x + nd.rightX * s * off;
      const tz = nd.z + nd.rightZ * s * off;
      const tint = m.towers[(si * 2 + (s > 0 ? 1 : 0)) % m.towers.length];
      // Tower grounded on the REAL terrain (a fixed 2.5 u sink left the
      // bottom floating up to ~2.7 u where the deck rides high — measured
      // from the block side); its slight overhang past the facade line
      // reads as a jutting palazzo bay. With the wider footprint one centre
      // sample is not enough (the buildQuarter lesson) — take the LOWEST of
      // the four corners so no corner can show daylight under it.
      let gy = track.heightAt(tx, tz);
      for (const [ox, oz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) {
        gy = Math.min(
          gy,
          track.heightAt(
            tx + nd.rightX * ((ox * TW) / 2) + fx * ((oz * TD) / 2),
            tz + nd.rightZ * ((ox * TW) / 2) + fz * ((oz * TD) / 2),
          ),
        );
      }
      gy -= 0.4;
      const top = nd.y + 8.8;
      g.add(box(TW, top - gy, TD, tint, tx, gy + (top - gy) / 2, tz, nd.dir));
      g.add(box(TW + 0.3, 0.14, TD + 0.3, m.terra, tx, nd.y + 8.87, tz, nd.dir));
      // The cornice at deck level: the ride passes over this house's roofline,
      // so the tower reads as the storeys ABOVE a block you are riding.
      g.add(box(TW + 0.26, 0.16, TD + 0.26, m.cotto, tx, nd.y + 0.06, tz, nd.dir));

      // Openings floor by floor, plus a doorway at the foot on the two faces
      // the block side can see.
      const floors: number[] = [];
      for (let y = gy + 2.6; y < top - 1.7; y += 3.0) floors.push(y);
      boxOpenings(
        { glass, shutters, stone },
        tx,
        tz,
        nd.dir,
        TW / 2,
        TD / 2,
        floors,
        si * 61 + (s > 0 ? 3 : 0),
        gy + 1.5,
      );
    }
    // THE ROPE IS A CATENARY, not a rod (the same pass as the street lines in
    // scenery/laundryNapoli.ts): 8 segments riding the sag curve, each rolled
    // to its own slope. §6.1: with yaw = nd.dir a box's local +X IS the right
    // vector, so the span runs along X and `roll` tips it in the vertical
    // plane. Welded = one draw call for the whole line.
    const sagAt = (t: number): number => (1 - t * t) * 0.5;
    const ropeY = (t: number): number => lineY - sagAt(t);
    const rope: BoxPart[] = [];
    const SEG = 8;
    for (let k = 0; k < SEG; k++) {
      const t0 = -1 + (2 * k) / SEG;
      const t1 = -1 + (2 * (k + 1)) / SEG;
      const flat = (t1 - t0) * off;
      const dy = ropeY(t1) - ropeY(t0);
      const tm = (t0 + t1) / 2;
      rope.push({
        w: Math.hypot(flat, dy),
        h: 0.06,
        d: 0.06,
        x: nd.x + nd.rightX * tm * off,
        y: (ropeY(t0) + ropeY(t1)) / 2,
        z: nd.z + nd.rightZ * tm * off,
        yaw: nd.dir,
        roll: Math.atan2(dy, flat),
      });
    }
    g.add(weld(rope, lineMat));
    // Cloths at ±offsets only — the centre stays clear (the laundry
    // convention, so the camera never flies straight through a sheet) — and
    // hung FROM the rope, never plumb (art() variety, no rng).
    for (const [ci, t] of [-0.72, -0.35, 0.35, 0.72].entries()) {
      const cx = nd.x + nd.rightX * t * off;
      const cz = nd.z + nd.rightZ * t * off;
      const cm = m.cloths[(si * 3 + ci) % m.cloths.length];
      const h = art(si * 31 + ci * 11);
      const cloth = new THREE.Mesh(new THREE.BoxGeometry(1.05, 1.15, 0.045), cm);
      cloth.position.set(cx, ropeY(t) - 0.6 - 0.575, cz);
      cloth.rotation.order = 'YXZ';
      cloth.rotation.y = nd.dir + (art(si * 17 + ci * 5) - 0.5) * 0.5;
      cloth.rotation.z = (h - 0.5) * 0.45;
      g.add(cloth);
    }
  }
  if (glass.length) g.add(weld(glass, m.iron));
  if (shutters.length) g.add(weld(shutters, m.shutter));
  if (stone.length) g.add(weld(stone, m.trim));
}

// ---- The rooftop five-a-side ----------------------------------------------

// Low-poly figures in the game's crowd language (scenery/crowd + builders
// buildCrowd): torso / head / legs / arms as four InstancedMeshes, so a whole
// team costs four draw calls. Geometry is shared at module scope like the
// crowd's. Posed STATICALLY — scenery is built once and never ticked.
const PLAYER_TORSO = new THREE.CylinderGeometry(0.22, 0.15, 0.8, 6);
const PLAYER_HEAD = new THREE.IcosahedronGeometry(0.18, 0);
const PLAYER_LEG = new THREE.BoxGeometry(0.13, 0.95, 0.16);
const PLAYER_ARM = new THREE.BoxGeometry(0.11, 0.62, 0.13);
/** Skin tones — mirrors scenery/palettes SKIN_TONES (duplicated locally per
 *  the batch-2 rule that this file imports nothing but shortcutMeshes). */
const SKINS = [0x4a2f1e, 0x5e3d28, 0x7a4f33, 0x95623d, 0xb27c4e, 0xcea074, 0xe7c39a, 0xf2d6b3];

type PitchPose = 'run' | 'strike' | 'keep';

/** A limb hangs from its pivot: the part matrix is T(pivot)·R(euler)·T(0,−h/2,0),
 *  so `rotX` swings a leg/arm about the hip/shoulder and `rotZ` splays it out.
 *  A limb's local down (0,−1,0) maps to (sin z, −cos x·cos z, −sin x), i.e.
 *  rotX < 0 throws the foot FORWARD and rotZ > 0 throws it to the +X side. */
interface PoseRig {
  /** y, z, pitch of the torso (leaning forward = +z, +pitch). */
  torso: [number, number, number];
  head: [number, number, number];
  hipY: number;
  legs: readonly [readonly [number, number, number], readonly [number, number, number]];
  shoulderY: number;
  arms: readonly [readonly [number, number, number], readonly [number, number, number]];
}
const POSES: Record<PitchPose, PoseRig> = {
  // Mid-stride: both feet off the deck, arms swinging opposite the legs.
  run: {
    torso: [1.28, 0.16, 0.3],
    head: [1.84, 0.33, 0.2],
    hipY: 0.96,
    legs: [
      [-0.11, -0.62, 0.02],
      [0.11, 0.55, -0.02],
    ],
    shoulderY: 1.58,
    arms: [
      [-0.27, 0.7, -0.12],
      [0.27, -0.75, 0.12],
    ],
  },
  // Striking: planted left, right leg swung high through the ball, body
  // rocked back off the shot, both arms out for balance.
  strike: {
    torso: [1.3, 0.02, -0.14],
    head: [1.88, 0.06, -0.05],
    hipY: 0.98,
    legs: [
      [-0.12, 0.35, 0.06],
      [0.12, -1.25, -0.07],
    ],
    shoulderY: 1.6,
    arms: [
      [-0.28, -0.15, -0.95],
      [0.28, -0.2, 0.85],
    ],
  },
  // Keeper set: crouched, legs splayed, arms wide.
  keep: {
    torso: [0.94, 0.16, 0.5],
    head: [1.44, 0.36, 0.34],
    hipY: 0.88,
    legs: [
      [-0.16, -0.2, -0.44],
      [0.16, -0.2, 0.44],
    ],
    shoulderY: 1.24,
    arms: [
      [-0.26, -0.25, -1.25],
      [0.26, -0.3, 1.25],
    ],
  },
};

const AZZURRO = 0x2f96d8; // the '87 sky blue (the catalog's Partenopei kit)
const BIANCO = 0xeef1f4;
const DARKSHORT = 0x232a33;

/** The game in progress. `a` runs down the pitch, `b` across (negative b is
 *  the touchline nearest the deck); `face` is a yaw offset from the ride's
 *  heading, so 0 looks down-course and π back up it. */
interface PitchPlayer {
  a: number;
  b: number;
  face: number;
  pose: PitchPose;
  shirt: number;
  shorts: number;
}
const SQUAD: readonly PitchPlayer[] = [
  { a: -13.2, b: 0.3, face: 0, pose: 'keep', shirt: 0x2fbf6a, shorts: DARKSHORT },
  { a: 13.3, b: -0.4, face: Math.PI, pose: 'keep', shirt: 0xf2c422, shorts: DARKSHORT },
  { a: 6.2, b: -1.6, face: 0.18, pose: 'strike', shirt: AZZURRO, shorts: BIANCO },
  { a: 8.7, b: -3.1, face: Math.PI - 0.22, pose: 'run', shirt: BIANCO, shorts: DARKSHORT },
  { a: 2.0, b: 3.4, face: 0.36, pose: 'run', shirt: AZZURRO, shorts: BIANCO },
  { a: -0.9, b: 1.7, face: -0.42, pose: 'run', shirt: BIANCO, shorts: DARKSHORT },
  { a: -5.4, b: -3.9, face: 0.12, pose: 'run', shirt: AZZURRO, shorts: BIANCO },
  { a: -8.3, b: 1.0, face: 0.26, pose: 'run', shirt: BIANCO, shorts: DARKSHORT },
  { a: 10.4, b: 3.8, face: -0.16, pose: 'run', shirt: AZZURRO, shorts: BIANCO },
];

/**
 * THE ROOFTOP PITCH (M117f, the owner's headline ask): a caged five-a-side
 * terrace alongside the deck with a game in progress, sized and sited so it
 * fills the frame while you ride (see the PITCH block for the measurements).
 * Everything here is deterministic (art() hashes), like all shortcut art.
 */
function buildRooftopPitch(track: Track, sc: PlacedShortcut, g: THREE.Group, m: Mats): void {
  const ch = sc.channel;
  const nd = ch.nodes[nodeAt(ch, PITCH.at)];
  const fx = Math.sin(nd.dir);
  const fz = Math.cos(nd.dir);
  const sx = nd.rightX * PITCH.side; // across, positive = AWAY from the deck
  const sz = nd.rightZ * PITCH.side;
  const cx = nd.x + sx * PITCH.off;
  const cz = nd.z + sz * PITCH.off;
  const yaw = nd.dir;
  /** Pitch frame → world: `a` down the pitch (= down-course), `b` across. */
  const P = (a: number, b: number): [number, number] => [cx + fx * a + sx * b, cz + fz * a + sz * b];

  const halfL = PITCH.long / 2;
  const halfW = PITCH.wide / 2;
  const slabL = PITCH.long + PITCH.margin * 2;
  const slabW = PITCH.wide + PITCH.margin * 2;

  // The terrace sits below the LOWEST deck node over its span, so the player
  // looks DOWN onto the game for the whole approach even though the deck
  // falls ~1 u across the pitch's length.
  let deckLow = Infinity;
  for (let d = PITCH.at - halfL - 6; d <= PITCH.at + halfL + 6; d += 3) {
    deckLow = Math.min(deckLow, ch.nodes[nodeAt(ch, d)].y);
  }
  const roofY = deckLow - PITCH.drop;
  const turfY = roofY + 0.22;
  let gLow = Infinity;
  for (const a of [-slabL / 2, 0, slabL / 2]) {
    for (const b of [-slabW / 2, slabW / 2]) {
      const [x, z] = P(a, b);
      gLow = Math.min(gLow, track.heightAt(x, z));
    }
  }
  const baseY = gLow - 0.7;

  // ---- The building it stands on ---------------------------------------
  // It wears the deck's own painted pastel facade, on cloned textures so the
  // repeat can suit a box face without touching the deck strips' UVs (three
  // keeps the UV transform per-texture and the pixels per shared Source, so
  // the clone costs a uniform, not an upload).
  const blockMat = m.facade.clone();
  if (m.facade.map) {
    blockMat.map = m.facade.map.clone();
    blockMat.map.repeat.set(1.7, 1);
    blockMat.map.needsUpdate = true;
  }
  if (m.facade.emissiveMap) {
    blockMat.emissiveMap = m.facade.emissiveMap.clone();
    blockMat.emissiveMap.repeat.set(1.7, 1);
    blockMat.emissiveMap.needsUpdate = true;
  }
  blockMat.side = THREE.FrontSide;
  g.add(box(slabW, roofY - baseY, slabL, blockMat, cx, (baseY + roofY) / 2, cz, yaw));
  g.add(box(slabW + 0.55, 0.3, slabL + 0.55, m.cotto, cx, roofY + 0.06, cz, yaw)); // coping
  g.add(box(slabW, 0.2, slabL, m.grimy, cx, roofY + 0.11, cz, yaw)); // terrace screed

  // ---- The playing surface ---------------------------------------------
  {
    const turf = new THREE.Mesh(new THREE.PlaneGeometry(PITCH.wide, PITCH.long), m.turf);
    turf.position.set(cx, turfY, cz);
    turf.rotation.order = 'YXZ';
    turf.rotation.y = yaw;
    turf.rotation.x = -Math.PI / 2;
    g.add(turf);
  }

  // ---- The cage: four chain-link walls, ONE welded quad soup ------------
  {
    const cw = halfW + 0.75;
    const cl = halfL + 0.75;
    const corners: [number, number][] = [
      [-cl, -cw],
      [cl, -cw],
      [cl, cw],
      [-cl, cw],
    ];
    const geos: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 4; i++) {
      const [a1, b1] = corners[i];
      const [a2, b2] = corners[(i + 1) % 4];
      const [x1, z1] = P(a1, b1);
      const [x2, z2] = P(a2, b2);
      geos.push(wallQuad(x1, z1, x2, z2, turfY, PITCH.cage, 1.35));
    }
    const cage = new THREE.Mesh(mergeGeometries(geos, false) as THREE.BufferGeometry, m.chain);
    cage.matrixAutoUpdate = false;
    g.add(cage);
    for (const gg of geos) gg.dispose();
  }

  // ---- Metalwork: cage posts + top rails + both goal frames -------------
  const white: BoxPart[] = [];
  const iron: BoxPart[] = [];
  {
    const cw = halfW + 0.75;
    const cl = halfL + 0.75;
    for (const a of [-cl, -cl / 2, 0, cl / 2, cl]) {
      for (const b of [-cw, cw]) {
        const [x, z] = P(a, b);
        white.push({ w: 0.13, h: PITCH.cage + 0.16, d: 0.13, x, y: turfY + PITCH.cage / 2, z, yaw });
      }
    }
    for (const b of [-cw, cw]) {
      const [x, z] = P(0, b);
      white.push({ w: 0.09, h: 0.09, d: cl * 2, x, y: turfY + PITCH.cage, z, yaw });
    }
    for (const a of [-cl, cl]) {
      const [x, z] = P(a, 0);
      white.push({ w: cw * 2, h: 0.09, d: 0.09, x, y: turfY + PITCH.cage, z, yaw });
    }
    // Goals: 3.2 wide × 2.0 tall at each end.
    for (const end of [-1, 1] as const) {
      const ga = end * (halfL - 0.75);
      for (const b of [-1.6, 1.6]) {
        const [x, z] = P(ga, b);
        white.push({ w: 0.12, h: 2.0, d: 0.12, x, y: turfY + 1.0, z, yaw });
      }
      const [gx, gz] = P(ga, 0);
      white.push({ w: 3.32, h: 0.12, d: 0.12, x: gx, y: turfY + 2.0, z: gz, yaw });
    }
  }

  // Goal nets: one sloping sheet per goal, welded into a single mesh.
  {
    const geos: THREE.BufferGeometry[] = [];
    for (const end of [-1, 1] as const) {
      const ga = end * (halfL - 0.75);
      const [x1, z1] = P(ga + end * 1.5, -1.6);
      const [x2, z2] = P(ga + end * 1.5, 1.6);
      const gg = wallQuad(x1, z1, x2, z2, turfY, 1.1, 0.75);
      // Rake the top edge back to the crossbar so the sheet reads as a net.
      const pos = gg.getAttribute('position') as THREE.BufferAttribute;
      for (const vi of [2, 3]) {
        pos.setY(vi, turfY + 2.0);
        pos.setX(vi, pos.getX(vi) - fx * end * 1.5);
        pos.setZ(vi, pos.getZ(vi) - fz * end * 1.5);
      }
      gg.computeVertexNormals();
      geos.push(gg);
    }
    const nets = new THREE.Mesh(mergeGeometries(geos, false) as THREE.BufferGeometry, m.goalNet);
    nets.matrixAutoUpdate = false;
    g.add(nets);
    for (const gg of geos) gg.dispose();
  }

  // ---- Floodlights, bench, bottle --------------------------------------
  const lamps: BoxPart[] = [];
  for (const a of [-1, 1]) {
    for (const b of [-1, 1]) {
      const px = a * (slabL / 2 - 1.0);
      const pb = b * (slabW / 2 - 1.0);
      const [x, z] = P(px, pb);
      iron.push({ w: 0.16, h: 4.7, d: 0.16, x, y: turfY + 2.35, z, yaw });
      // Dark housing with a small pale lens under it — a bare emissive slab
      // read as a white paddle stuck on a pole in noon light (measured).
      const [hx, hz] = P(px - a * 0.32, pb - b * 0.32);
      iron.push({ w: 0.62, h: 0.3, d: 0.38, x: hx, y: turfY + 4.66, z: hz, yaw, roll: -b * 0.4 });
      lamps.push({ w: 0.44, h: 0.07, d: 0.26, x: hx, y: turfY + 4.5, z: hz, yaw, roll: -b * 0.4 });
    }
  }
  {
    // Bench on the deck-side touchline (a little foreground on the way past).
    const b = -(halfW + 1.7);
    const [x, z] = P(0, b);
    g.add(box(0.55, 0.14, 5.4, m.timber, x, turfY + 0.62, z, yaw));
    g.add(box(0.16, 0.5, 5.4, m.timber, x + sx * 0.28, turfY + 0.92, z + sz * 0.28, yaw));
    for (const a of [-2.2, 2.2]) {
      const [lx, lz] = P(a, b);
      iron.push({ w: 0.1, h: 0.62, d: 0.5, x: lx, y: turfY + 0.31, z: lz, yaw });
    }
    const [bx, bz] = P(3.1, b);
    const bottle = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.11, 0.33, 6), m.tank);
    bottle.position.set(bx, turfY + 0.17, bz);
    g.add(bottle);
  }
  g.add(weld(white, m.white));
  g.add(weld(iron, m.iron));
  g.add(weld(lamps, m.lamp));

  // ---- The game ---------------------------------------------------------
  {
    const n = SQUAD.length;
    const torsos = new THREE.InstancedMesh(PLAYER_TORSO, new THREE.MeshStandardMaterial({ flatShading: true, roughness: 0.85 }), n);
    const heads = new THREE.InstancedMesh(PLAYER_HEAD, new THREE.MeshStandardMaterial({ flatShading: true, roughness: 0.7 }), n);
    const legs = new THREE.InstancedMesh(PLAYER_LEG, new THREE.MeshStandardMaterial({ flatShading: true, roughness: 0.9 }), n * 2);
    const arms = new THREE.InstancedMesh(PLAYER_ARM, new THREE.MeshStandardMaterial({ flatShading: true, roughness: 0.85 }), n * 2);
    const person = new THREE.Object3D();
    const pivot = new THREE.Object3D();
    const hang = new THREE.Object3D();
    const col = new THREE.Color();
    const tmp = new THREE.Matrix4();
    SQUAD.forEach((p, i) => {
      const rig = POSES[p.pose];
      const [x, z] = P(p.a, p.b);
      person.position.set(x, turfY, z);
      person.rotation.set(0, yaw + p.face, 0);
      person.scale.setScalar(0.96 + art(i * 13) * 0.1);
      person.updateMatrix();
      const part = (px: number, py: number, pz: number, rot: number): THREE.Matrix4 => {
        pivot.position.set(px, py, pz);
        pivot.rotation.set(rot, 0, 0);
        pivot.scale.setScalar(1);
        pivot.updateMatrix();
        return tmp.multiplyMatrices(person.matrix, pivot.matrix).clone();
      };
      const limb = (px: number, py: number, rx2: number, rz2: number, half: number): THREE.Matrix4 => {
        pivot.position.set(px, py, 0);
        pivot.rotation.set(rx2, 0, rz2);
        pivot.scale.setScalar(1);
        pivot.updateMatrix();
        hang.position.set(0, -half, 0);
        hang.rotation.set(0, 0, 0);
        hang.updateMatrix();
        return tmp.multiplyMatrices(person.matrix, pivot.matrix).multiply(hang.matrix).clone();
      };
      torsos.setMatrixAt(i, part(0, rig.torso[0], rig.torso[1], rig.torso[2]));
      torsos.setColorAt(i, col.set(p.shirt));
      heads.setMatrixAt(i, part(0, rig.head[0], rig.head[1], rig.head[2]));
      heads.setColorAt(i, col.set(SKINS[Math.floor(art(i * 7 + 3) * SKINS.length) % SKINS.length]));
      rig.legs.forEach((l, li) => {
        const k2 = i * 2 + li;
        legs.setMatrixAt(k2, limb(l[0], rig.hipY, l[1], l[2], 0.475));
        legs.setColorAt(k2, col.set(p.shorts));
      });
      rig.arms.forEach((l, li) => {
        const k2 = i * 2 + li;
        arms.setMatrixAt(k2, limb(l[0], rig.shoulderY, l[1], l[2], 0.31));
        arms.setColorAt(k2, col.set(SKINS[Math.floor(art(i * 7 + 3) * SKINS.length) % SKINS.length]));
      });
    });
    for (const inst of [torsos, heads, legs, arms]) {
      inst.instanceMatrix.needsUpdate = true;
      if (inst.instanceColor) inst.instanceColor.needsUpdate = true;
      g.add(inst);
    }
  }
  {
    // The match ball, just ahead of the striker's boot, plus a spare by the
    // bench (welded into one mesh).
    const geos: THREE.BufferGeometry[] = [];
    for (const [a, b, r] of [
      [7.7, -1.15, 0.34],
      [3.9, -(halfW + 1.55), 0.3],
    ] as const) {
      const [x, z] = P(a, b);
      const gg = new THREE.IcosahedronGeometry(r, 1);
      gg.translate(x, turfY + r, z);
      geos.push(gg);
    }
    const ball = new THREE.Mesh(mergeGeometries(geos, false) as THREE.BufferGeometry, m.ball);
    ball.matrixAutoUpdate = false;
    g.add(ball);
    for (const gg of geos) gg.dispose();
  }

  // ---- Neapolitan roof dressing outside the cage ------------------------
  {
    const b = slabW / 2 - 1.3;
    const [hx, hz] = P(-(slabL / 2 - 1.5), b);
    g.add(box(1.4, 1.15, 1.15, m.towers[2], hx, roofY + 0.78, hz, yaw)); // stair hut
    g.add(box(1.6, 0.12, 1.35, m.cotto, hx, roofY + 1.41, hz, yaw));
    for (const [a, dx] of [
      [slabL / 2 - 1.6, 0],
      [slabL / 2 - 2.3, 0.35],
    ] as const) {
      const [px, pz] = P(a, b + dx);
      const pot = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.17, 0.5, 6), m.terra);
      pot.position.set(px, roofY + 0.46, pz);
      g.add(pot);
      g.add(box(0.55, 0.5, 0.55, m.grimy, px, roofY + 0.26, pz, yaw));
    }
    const [ax, az] = P(2.4, b);
    g.add(box(0.06, 2.2, 0.06, m.iron, ax, roofY + 1.3, az, yaw)); // aerial
    g.add(box(0.95, 0.045, 0.045, m.iron, ax, roofY + 2.05, az, yaw + 0.4));
  }
}

// ---- The quarter under the ride -------------------------------------------

/** Free-standing old-town houses beside the spine — the block interior is
 *  EMPTY ground by construction (zone builders only work near the road, and
 *  the corridor clearing scrubs 14 u around the spine anyway), so the
 *  "riding over the quarter" story is built here deliberately (the Lagos
 *  stilt-hut precedent). Roofs sit BELOW the deck floor so the ride stays
 *  visibly OVER the rooftops. */
function buildQuarter(track: Track, sc: PlacedShortcut, g: THREE.Group, m: Mats): void {
  const ch = sc.channel;
  const nEnd = nodeAt(ch, ch.length - tuning.shortcuts.stubLen - 1);
  const stride = Math.max(2, Math.round(24 / ch.step));
  // M117g: the houses were untextured plaster boxes with a cotto lid, which
  // from the deck read as shipping containers dropped on a lawn (QA:
  // block-courtyard, deck-skirt-street). Shuttered windows, welded once for the
  // whole quarter, are what make them houses.
  const open: OpenParts = { glass: [], shutters: [], stone: [] };
  for (let i = nodeAt(ch, 28), hk = 0; i < nEnd - Math.round(42 / ch.step); i += stride, hk++) {
    const nd = ch.nodes[i];
    const s = hk % 2 === 0 ? -1 : 1; // alternate sides of the deck
    // THE PITCH WINDOW (M117f): the rooftop terrace stands at offset 12..29
    // on PITCH.side, and the quarter's houses reach offset 19.5 — they would
    // grow straight through it. Clear the footprint AND the approach the
    // camera looks along (a house at d≈99 sits between the eye and the pitch
    // and its roof rides 1 u under the deck, so it clips the near touchline).
    if (s === PITCH.side) {
      const d = i * ch.step;
      if (d > PITCH.at - 58 && d < PITCH.at + 26) continue;
    }
    const off = 9.5 + art(i * 7 + 29) * 5.5;
    const px = nd.x + nd.rightX * s * off;
    const pz = nd.z + nd.rightZ * s * off;
    // Never crowd the ROAD corridor (the entry/exit stretches run near it).
    // Hint-less project = full scan (the M99 rule: a hinted project reads
    // clamped bogus arcs for far-away objects).
    const pr = track.project(px, pz);
    if (Math.abs(pr.lateral) < track.halfWidth + 9) continue;
    // The bay guard — a house planted on the lungomare's water stood in the sea.
    if (overBay(track, sc, px, pz)) continue;
    const gy = track.heightAt(px, pz);
    const clear = nd.y - gy;
    if (clear < 3.2) continue; // near the exit the deck is already roof-low
    const w = 6 + art(i * 11 + 3) * 3;
    const dep = 6 + art(i * 13 + 5) * 3;
    const h = Math.min(clear - 0.7 - art(i * 17) * 1.2, 6.8);
    if (h < 2.2) continue;
    const yaw = nd.dir + (art(i * 19) - 0.5) * 0.5;
    const tint = m.towers[Math.floor(art(i * 23) * m.towers.length) % m.towers.length];
    // GROUNDING (QA: houses floated over dips): one heightAt at the centre is
    // not enough on this terrain — sample all four footprint corners and take
    // the LOWEST, then extend the walls down to it. The roof stays exactly
    // where the clearance maths put it (gy + h), so the "roofs sit BELOW the
    // deck floor" contract is untouched; only the skirt grows. §6.1: with
    // rotation.y = yaw a box's local +X is world (cos yaw, −sin yaw) and its
    // local +Z is (sin yaw, cos yaw).
    const cs = Math.cos(yaw);
    const sn = Math.sin(yaw);
    let base = gy;
    for (const [ox, oz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) {
      const cx = px + (ox * w) / 2 * cs + (oz * dep) / 2 * sn;
      const cz = pz - (ox * w) / 2 * sn + (oz * dep) / 2 * cs;
      base = Math.min(base, track.heightAt(cx, cz));
    }
    const roof = gy + h;
    const wallH = roof - base + 0.25; // 0.25 buried, so no corner shows daylight
    g.add(box(w, wallH, dep, tint, px, roof - wallH / 2, pz, yaw));
    g.add(box(w + 0.6, 0.28, dep + 0.6, m.cotto, px, roof + 0.12, pz, yaw));
    if (art(i * 29 + 7) < 0.5) {
      const pot = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.16, 0.45, 6), m.terra);
      pot.position.set(px + Math.cos(yaw) * w * 0.25, gy + h + 0.48, pz - Math.sin(yaw) * w * 0.25);
      g.add(pot);
    }
    const floors: number[] = [];
    for (let y = gy + 1.55; y < roof - 1.05; y += 2.4) floors.push(y);
    boxOpenings(open, px, pz, yaw, w / 2, dep / 2, floors, i * 53 + 7);
  }
  if (open.glass.length) g.add(weld(open.glass, m.iron));
  if (open.shutters.length) g.add(weld(open.shutters, m.shutter));
  if (open.stone.length) g.add(weld(open.stone, m.trim));
}

// ---- The courtyard floor under the ride -----------------------------------

/** THE GROUND YOU LOOK DOWN ON. The block interior is empty by construction
 *  (zone builders only work near the road, and the corridor clearing scrubs
 *  14 u around the spine), so riding the roofs meant overlooking a bare green
 *  field (QA: rooftop-deck-aerial). This lays the quarter's floor — sanpietrini
 *  courtyards and alleys — as ONE terrain-following ribbon under the whole run.
 *
 *  Terrain-following is the point: chStrip samples the ground at BOTH edges per
 *  node, so the sheet can never poke through a rise or float over a dip the way
 *  a flat slab would. Half-width 12 stays inside the clearing's 14 u
 *  right-of-way.
 *
 *  MEASURED GOTCHA: the first pass kept ONE ribbon and collapsed the excluded
 *  nodes to a single buried point. That is not free — the quads bridging a
 *  full-width node to a collapsed one become long dark slivers fanning across
 *  the grass (visible from the deck, QA). The sheet is emitted as SEPARATE
 *  ribbons per contiguous run of valid nodes instead (the water.ts runs
 *  pattern), so an excluded stretch produces no geometry at all. Excluded =
 *  over the bay, or anywhere near the road, whose own surface owns that ground
 *  and would z-fight with a second sheet laid over it. */
function buildCourtyard(track: Track, sc: PlacedShortcut, g: THREE.Group, m: Mats): void {
  const ch = sc.channel;
  const nEnd = nodeAt(ch, ch.length - tuning.shortcuts.stubLen - 1);
  const HALF = 12;
  const edges = (nd: (typeof ch.nodes)[number]) => ({
    lx: nd.x - nd.rightX * HALF,
    lz: nd.z - nd.rightZ * HALF,
    rx: nd.x + nd.rightX * HALF,
    rz: nd.z + nd.rightZ * HALF,
  });
  const near = (x: number, z: number): boolean =>
    Math.abs(track.project(x, z).lateral) < track.halfWidth + 4;
  const ok: boolean[] = [];
  for (let i = 0; i <= nEnd; i++) {
    const e = edges(ch.nodes[i]);
    ok.push(
      !overBay(track, sc, e.lx, e.lz) &&
        !overBay(track, sc, e.rx, e.rz) &&
        !near(e.lx, e.lz) &&
        !near(e.rx, e.rz),
    );
  }
  const verts = (nd: (typeof ch.nodes)[number]): [THREE.Vector3, THREE.Vector3] => {
    const e = edges(nd);
    return [
      new THREE.Vector3(e.lx, track.heightAt(e.lx, e.lz) + 0.08, e.lz),
      new THREE.Vector3(e.rx, track.heightAt(e.rx, e.rz) + 0.08, e.rz),
    ];
  };
  for (let i = 0; i <= nEnd; i++) {
    if (!ok[i]) continue;
    let j = i;
    while (j + 1 <= nEnd && ok[j + 1]) j++;
    if (j > i) g.add(chStrip(ch.nodes, ch.step, i, j, verts, m.pave, 3));
    i = j;
  }

  // ---- COURTYARD LIFE (M117g nit, QA: block-courtyard, block-houses-check —
  // "the block interior is a near-featureless lime field"). The paving alone
  // said "there is ground here"; it did not say "people live here". These are
  // the things a Neapolitan cortile actually holds: the low walls that divide
  // one yard from the next, potted lemons along them, a wellhead, stacked
  // crates and a ground-level washing line answering the ones overhead.
  //
  // Everything is placed off art() (no rng draw, so no city layout moves),
  // guarded by the SAME ok[] the paving uses (never on water, never near the
  // road), and welded per material — the whole quarter's dressing is four
  // draw calls.
  const stucco: BoxPart[] = [];
  const flags: BoxPart[] = [];
  const terra: BoxPart[] = [];
  const timber: BoxPart[] = [];
  const iron: BoxPart[] = [];
  const stride = Math.max(2, Math.round(11 / ch.step));
  for (let i = stride, k = 0; i < nEnd - 2; i += stride, k++) {
    if (!ok[i] || !ok[i - 1] || !ok[i + 1]) continue;
    const nd = ch.nodes[i];
    const s = art(i * 3 + 5) < 0.5 ? -1 : 1;
    const off = 4.6 + art(i * 7 + 11) * 5.6; // 4.6..10.2 — on the paving, clear of the skirt
    const fx = Math.sin(nd.dir);
    const fz = Math.cos(nd.dir);
    const px = nd.x + nd.rightX * s * off;
    const pz = nd.z + nd.rightZ * s * off;
    const gy = track.heightAt(px, pz);
    const kind = k % 4;
    if (kind === 0) {
      // A dividing wall running ACROSS the yard, with its cotto coping.
      const len = 4.5 + art(i * 11) * 3.5;
      stucco.push({ w: 3.0, h: 1.15, d: 0.34, x: px, y: gy + 0.5, z: pz, yaw: nd.dir });
      stucco.push({ w: 0.34, h: 1.15, d: len, x: px + fx * (len / 2), y: gy + 0.5, z: pz + fz * (len / 2), yaw: nd.dir });
      terra.push({ w: 0.46, h: 0.1, d: len, x: px + fx * (len / 2), y: gy + 1.12, z: pz + fz * (len / 2), yaw: nd.dir });
      for (let p = 0; p < 3; p++) {
        const t = 0.7 + p * (len / 3);
        terra.push({
          w: 0.44,
          h: 0.5,
          d: 0.44,
          x: px + fx * t + nd.rightX * s * 0.55,
          y: gy + 0.25,
          z: pz + fz * t + nd.rightZ * s * 0.55,
          yaw: nd.dir + art(i * 13 + p) * 0.6,
        });
      }
    } else if (kind === 1) {
      // The wellhead: a stone drum under an iron hoop.
      stucco.push({ w: 1.5, h: 0.95, d: 1.5, x: px, y: gy + 0.45, z: pz, yaw: nd.dir + 0.3 });
      terra.push({ w: 1.75, h: 0.13, d: 1.75, x: px, y: gy + 0.98, z: pz, yaw: nd.dir + 0.3 });
      for (const sd of [-1, 1] as const)
        iron.push({ w: 0.1, h: 1.5, d: 0.1, x: px + fx * sd * 0.62, y: gy + 1.7, z: pz + fz * sd * 0.62, yaw: nd.dir });
      iron.push({ w: 0.1, h: 0.1, d: 1.4, x: px, y: gy + 2.42, z: pz, yaw: nd.dir });
    } else if (kind === 2) {
      // A stack of crates and a couple of pallets against the paving.
      for (let c = 0; c < 3; c++) {
        const a2 = art(i * 17 + c * 5);
        timber.push({
          w: 0.85 + a2 * 0.3,
          h: 0.62,
          d: 0.9,
          x: px + fx * (c * 0.5 - 0.5) + nd.rightX * s * (a2 - 0.5) * 0.7,
          y: gy + 0.31 + (c === 2 ? 0.62 : 0),
          z: pz + fz * (c * 0.5 - 0.5) + nd.rightZ * s * (a2 - 0.5) * 0.7,
          yaw: nd.dir + (a2 - 0.5) * 1.1,
        });
      }
      terra.push({ w: 0.5, h: 0.55, d: 0.5, x: px + fx * 1.9, y: gy + 0.27, z: pz + fz * 1.9, yaw: nd.dir });
    } else {
      // A ground-level washing line between two poles — the same story as the
      // spans overhead, told at yard level.
      const len = 5.0 + art(i * 19) * 2.5;
      for (const sd of [-1, 1] as const)
        iron.push({ w: 0.11, h: 2.5, d: 0.11, x: px + fx * sd * (len / 2), y: gy + 1.25, z: pz + fz * sd * (len / 2), yaw: nd.dir });
      iron.push({ w: 0.05, h: 0.05, d: len, x: px, y: gy + 2.36, z: pz, yaw: nd.dir });
      // A swept flag-paved patch, so not every yard wears the same setts.
      flags.push({ w: 4.2, h: 0.09, d: len + 1.2, x: px, y: gy + 0.13, z: pz, yaw: nd.dir });
    }
  }
  if (stucco.length) g.add(weld(stucco, m.plaster));
  if (flags.length) g.add(weld(flags, m.trim));
  if (terra.length) g.add(weld(terra, m.terra));
  if (timber.length) g.add(weld(timber, m.timber));
  if (iron.length) g.add(weld(iron, m.iron));
}

// ---- The collapsed-roof exit ----------------------------------------------

/** THE COLLAPSED ROOF rides the chute itself: rubble piles, spilled tile
 *  shards and leaning timber shores live ON the final descent's outer
 *  bands, above the broken parapet crest. MEASURED: ground-level berms
 *  flanking the opening were invisible — Napoli's exit sits exactly where
 *  the bayfront begins, so the verge beyond the wall is the SEA (a berm
 *  planted there stood in the water) and anything lower than ~2 u hides
 *  behind the sea wall anyway. Debris on the deck's own flanks is always
 *  visible through the opening and can never touch water or road. */
function buildCollapsedExit(track: Track, sc: PlacedShortcut, g: THREE.Group, m: Mats): void {
  dressExit(g, sc, m.plaster);
  const ch = sc.channel;
  const nEnd = nodeAt(ch, ch.length - tuning.shortcuts.stubLen - 1);

  // CLOSE THE BLOCK'S DOWNHILL END (QA: rooftop-exit). The skirt thins out as
  // the chute reaches grade, so the last houses faded into nothing and you
  // looked straight past the block into bare ground. Cap it with a BROKEN
  // HOUSE END: a cross wall the full roof width at the last node that still
  // has real height under it, wearing the ground-floor paint, with a cotto
  // lid and tufa rubble spilling out of the break.
  {
    const roofHalf = ch.halfWidth + 0.35 + ROOF_OVERHANG;
    let i = nEnd;
    while (i > 4 && ch.nodes[i].y - track.heightAt(ch.nodes[i].x, ch.nodes[i].z) < 1.6) i--;
    const nd = ch.nodes[i];
    const gy = track.heightAt(nd.x, nd.z);
    const h = nd.y - gy + 0.3;
    if (h > 1.4 && i > 4) {
      g.add(box(roofHalf * 2, h, 0.7, m.street, nd.x, gy - 0.3 + h / 2, nd.z, nd.dir));
      g.add(box(roofHalf * 2 + 0.5, 0.24, 1.0, m.cotto, nd.x, nd.y + 0.1, nd.z, nd.dir));
      for (let k = 0; k < 5; k++) {
        const t = (k / 4 - 0.5) * 2 * roofHalf * 0.8;
        const r = 0.45 + art(k * 13 + i) * 0.5;
        const pile = new THREE.Mesh(new THREE.ConeGeometry(r, 0.4 + art(k * 7) * 0.45, 6), m.rubble);
        pile.position.set(
          nd.x + nd.rightX * t + Math.sin(nd.dir) * 0.9,
          gy + 0.18,
          nd.z + nd.rightZ * t + Math.cos(nd.dir) * 0.9,
        );
        pile.rotation.y = art(k * 17) * Math.PI;
        g.add(pile);
      }
    }
  }
  const from = Math.max(2, nEnd - Math.round(20 / ch.step));
  for (let i = from, k = 0; i < nEnd; i += 1, k++) {
    const nd = ch.nodes[i];
    const s = k % 2 === 0 ? -1 : 1;
    const off = 3.95 + art(i * 5 + 13) * 0.55;
    const px = nd.x + nd.rightX * s * off;
    const pz = nd.z + nd.rightZ * s * off;
    if (art(i * 3 + 5) < 0.55) {
      const r = 0.5 + art(i * 7 + 1) * 0.45;
      const pile = new THREE.Mesh(new THREE.ConeGeometry(r, 0.45 + art(i * 11) * 0.35, 6), m.rubble);
      pile.position.set(px, nd.y + 0.22, pz);
      pile.rotation.y = art(i * 13) * Math.PI;
      g.add(pile);
    }
    // Tile shards shaken loose down the run.
    g.add(
      box(0.5, 0.07, 0.32, m.terra, px + nd.rightX * s * -0.9, nd.y + 0.09, pz + nd.rightZ * s * -0.9, art(i * 17 + 3) * Math.PI),
    );
  }
  // Two timber shores leaning on the last intact parapet stretch.
  for (const s of [-1, 1] as const) {
    const i = Math.max(2, from - 2 - (s > 0 ? 2 : 0));
    const nd = ch.nodes[i];
    const px = nd.x + nd.rightX * s * 4.1;
    const pz = nd.z + nd.rightZ * s * 4.1;
    // Yaw across the course so the pitch tips the shore ONTO the parapet
    // (top toward −s·right); yawed along-course it leans down the run.
    g.add(box(0.15, 2.6, 0.15, m.timber, px, nd.y + 1.15, pz, nd.dir + Math.PI / 2, -0.42 * s));
  }
}

// ---- Entry ----------------------------------------------------------------

export function buildNapoliShortcut(track: Track, sc: PlacedShortcut, g: THREE.Group): void {
  const m = makeMats();
  if (sc.kicker) buildScaffoldRamp(track, sc, g, m);
  buildCourtyard(track, sc, g, m); // the floor first — everything else stands on it
  buildRooftopRun(track, sc, g, m);
  buildRoofLife(sc, g, m);
  buildLaundrySpans(track, sc, g, m);
  buildQuarter(track, sc, g, m);
  buildRooftopPitch(track, sc, g, m);
  buildCollapsedExit(track, sc, g, m);
}
